import type {
  CommissionItemKind,
  CommissionPackageMode,
  CommissionRate,
  EmploymentBond,
} from '@hue/contracts';
import type { GuideMarkStatus } from '@hue/contracts';
import type { Reimbursement } from '@hue/contracts';
import type { ContactNumber } from '@hue/contracts';
import type { SocialLink } from '@hue/contracts';
import type { PaymentInstructions } from '@hue/contracts';
import type {
  HealthDeclaration,
  HealthDivergence,
  HealthTheme,
  HealthThemeColor,
  OccurrenceSeverity,
  OccurrenceType,
  RecordAnswers,
  RecordBlock,
  RecordIssue,
  RecordSignaturePolicy,
  RecordTemplateKind,
  SchedulingCancellationPolicy,
  SchedulingCancellationSettings,
  SchedulingCancellationSnapshot,
  SchedulingDepositSettings,
  SignatureStroke,
} from '@hue/contracts';
import axios from 'axios';

const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
if (!apiUrl) throw new Error('VITE_API_URL não configurada.');

/// Lista em parâmetro vai repetida, sem colchetes (`status=A&status=B`): é o formato que o servidor
/// lê como escolha múltipla. Lista vazia não vai, e não mandar é "todos".
export const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  paramsSerializer: { indexes: null },
});

let refreshRequest: Promise<void> | undefined;
api.interceptors.response.use(undefined, async (error: unknown) => {
  if (!axios.isAxiosError(error) || error.response?.status !== 403 || !error.config) {
    throw error;
  }
  const request = error.config as typeof error.config & { _hueRetried?: boolean };
  const path = request.url ?? '';
  if (
    request._hueRetried ||
    path.includes('/auth/refresh') ||
    path.includes('/auth/login') ||
    path.includes('/auth/root/login') ||
    path.includes('/auth/owner-invitations')
  ) {
    throw error;
  }
  request._hueRetried = true;
  refreshRequest ??= axios
    .post(`${apiUrl}/auth/refresh`, undefined, { withCredentials: true })
    .then(() => undefined)
    .finally(() => {
      refreshRequest = undefined;
    });
  try {
    await refreshRequest;
    return await api.request(request);
  } catch {
    throw error;
  }
});

export interface UserAppearance {
  /// Organização do atendimento; ausente na gravação preserva a escolha anterior.
  appointmentLayout?: 'columns' | 'stacked' | undefined;
  mode: 'light' | 'dark';
  palette: 'hue' | 'mono' | 'navy' | 'moss' | 'wine' | 'straw' | 'blossom';
  density: 'comfortable' | 'compact';
  /// Menu lateral aberto ou recolhido; ausente vale aberto.
  navigation?: 'expanded' | 'collapsed' | undefined;
}

export interface SessionUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  /// Como a pessoa quer ser chamada (ADR 0067). Nulo vale o primeiro nome.
  preferredName?: string | null;
  phone?: string | null;
  platformRole: 'ROOT' | 'USER';
  timeZone: string | null;
  /// Nulo enquanto a pessoa nunca escolheu: vale o que este navegador guardou.
  appearance?: UserAppearance | null;
  context?:
    | { kind: 'PLATFORM'; roleKeys: string[]; permissions: string[]; isRoot: boolean }
    | { kind: 'TENANT_MEMBER'; membershipId: string }
    /// A pessoa como cliente, sem empresa (ADR 0067).
    | { kind: 'CUSTOMER' }
    | {
        kind: 'PLATFORM_ACTING_ON_TENANT';
        roleKeys: string[];
        permissions: string[];
        isRoot: true;
        actingReason: string | null;
      }
    | undefined;
  currentTenant: {
    id: string;
    displayName: string;
    key: string;
    /// O fuso operacional (ADR 0044): o da unidade ativa; sem unidade, o padrão da empresa.
    timeZone: string;
    /// O fuso padrão da empresa, que só vale para criar unidades.
    defaultTimeZone?: string;
    canManageSettings: boolean;
    roleKeys: string[];
    /// Já filtradas pela contratação no servidor: permissão de módulo que a empresa não tem não
    /// chega até aqui, e por isso a navegação esconde a área sem precisar checar mais nada.
    permissions: string[];
    /// A contratação efetiva, com o núcleo incluído.
    moduleKeys: string[];
    featureKeys: string[];
    businessUnits?: Array<{
      id: string;
      displayName: string;
      key: string;
      kind: BusinessUnitKind;
      timeZone?: string;
    }>;
    activeBusinessUnitId?: string | null;
    /// Alcança toda unidade; só quem alcança distribui unidades a outras pessoas.
    seesEveryUnit?: boolean;
  } | null;
}

/// Estabelecimento atende clientes e ocupa vaga do plano; apoio (depósito, escritório) guarda
/// estoque e pessoas, não tem agenda e fica fora do limite (ADR 0039).
export type BusinessUnitKind = 'ESTABLISHMENT' | 'SUPPORT';

export interface Tenant {
  id: string;
  displayName: string;
  legalName: string | null;
  key: string;
  timeZone: string;
  status: string;
  businessUnits: Array<{
    id: string;
    displayName: string;
    key: string;
    kind: BusinessUnitKind;
    active: boolean;
  }>;
}

/// Endereço de uma unidade, na mesma forma do cadastro do cliente.
export interface BusinessUnitAddress {
  country: string;
  postalCode: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
}

/// Uma faixa do horário de funcionamento, em minutos do dia civil no fuso da unidade (ADR 0074).
export interface OpeningHour {
  weekday: number;
  startMinute: number;
  endMinute: number;
}

/// A unidade com os dados físicos que vão ao timbre dos documentos (ADR 0040).
export type BusinessUnit = Tenant['businessUnits'][number] & {
  contactNumbers?: ContactNumber[];
  allowClientProfessionalChoice?: boolean;
  socialLinks?: SocialLink[];
  openingHours?: OpeningHour[];
  /// O fuso operacional da unidade (ADR 0044): o dia civil de agenda, estoque e caixa dela.
  timeZone: string;
  phone: string | null;
  email: string | null;
  taxId: string | null;
  address: BusinessUnitAddress | null;
};

export interface BusinessUnitInput {
  contactNumbers?: ContactNumber[];
  allowClientProfessionalChoice?: boolean;
  socialLinks?: SocialLink[];
  openingHours?: OpeningHour[];
  displayName: string;
  timeZone?: string;
  phone: string | null;
  email: string | null;
  taxId: string | null;
  address: BusinessUnitAddress | null;
}

export interface BusinessUnitManagement {
  units: BusinessUnit[];
  /// `null` é contratação sem teto de estabelecimentos.
  activeLimit: number | null;
}

export interface TenantFilters {
  search?: string | undefined;
  activity: 'ALL' | 'ACTIVE' | 'INACTIVE';
}

export interface PlatformPermission {
  key: string;
  category: string;
  displayName: string;
}

export interface PlatformRole {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  protected: boolean;
  privileged: boolean;
  active: boolean;
  permissionKeys: string[];
  userCount: number;
}

export interface PlatformAccess {
  id: string;
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  active: boolean;
  roles: Array<{ id: string; key: string; displayName: string; privileged: boolean }>;
}

export const platformAccessApi = {
  permissions: async (): Promise<PlatformPermission[]> =>
    (await api.get<{ permissions: PlatformPermission[] }>('/root/platform-permissions')).data
      .permissions,
  roles: async (): Promise<PlatformRole[]> =>
    (await api.get<{ roles: PlatformRole[] }>('/root/platform-roles')).data.roles,
  accesses: async (search?: string): Promise<PlatformAccess[]> =>
    (
      await api.get<{ accesses: PlatformAccess[] }>('/root/platform-accesses', {
        params: search ? { search } : undefined,
      })
    ).data.accesses,
  createRole: async (input: {
    displayName: string;
    description: string | null;
    permissionKeys: string[];
  }): Promise<PlatformRole> =>
    (await api.post<{ role: PlatformRole }>('/root/platform-roles', input)).data.role,
  updateRole: async (
    roleId: string,
    input: { displayName: string; description: string | null; permissionKeys: string[] },
  ): Promise<PlatformRole> =>
    (await api.patch<{ role: PlatformRole }>(`/root/platform-roles/${roleId}`, input)).data.role,
  deactivateRole: async (roleId: string): Promise<void> => {
    await api.delete(`/root/platform-roles/${roleId}`);
  },
  replaceRoles: async (input: { email: string; roleIds: string[] }): Promise<PlatformAccess> =>
    (await api.post<{ access: PlatformAccess }>('/root/platform-accesses', input)).data.access,
  deactivateAccess: async (userId: string): Promise<void> => {
    await api.delete(`/root/platform-accesses/${userId}`);
  },
};

export interface OwnerInvitationInspection {
  tenant: { id: string; displayName: string };
  user: { email: string; firstName: string; lastName: string };
  accountActive: boolean;
  mfaUri: string | null;
}

export type MembershipStatus = 'INVITED' | 'ACTIVE' | 'SUSPENDED' | 'DISABLED';

export interface CompanyMember {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: MembershipStatus;
  joinedAt: string | null;
  createdAt: string;
  roles: Array<{ id: string; key: string; displayName: string; protected: boolean }>;
  /// `ALL` vale também para as unidades criadas depois; `SELECTED`, só para as listadas.
  unitScope: 'ALL' | 'SELECTED';
  businessUnits: Array<{ id: string; displayName: string }>;
}

export interface AccessRole {
  id: string;
  key: string;
  displayName: string;
  description: string | null;
  protected: boolean;
  active: boolean;
  memberCount: number;
  permissionKeys: string[];
}

export interface CatalogPermission {
  key: string;
  displayName: string;
  /// O módulo dono e a parte vendável dele, quando houver. Serve para a tela avisar que um perfil
  /// concede algo que a empresa não contratou.
  moduleKey: string;
  featureKey: string | null;
}

export interface PermissionModule {
  /// Rótulo de agrupamento na tela. Não é o módulo vendável — "Sócios" e "Investimento inicial"
  /// aparecem separados aqui e são o mesmo produto.
  module: string;
  permissions: CatalogPermission[];
}

export interface AccessRoleInput {
  displayName: string;
  description: string | null;
  permissionKeys: string[];
}

export type Currency = 'BRL' | 'USD' | 'ARS';
export type BillingPeriod = 'MONTHLY' | 'YEARLY';

export interface PlanPrice {
  currency: Currency;
  period: BillingPeriod;
  amountCents: number;
}

export interface SellableFeature {
  key: string;
  displayName: string;
  description: string;
}

export interface SellableModule {
  key: string;
  displayName: string;
  description: string;
  sellable: boolean;
  features: SellableFeature[];
}

export interface Plan {
  key: string;
  displayName: string;
  description: string | null;
  active: boolean;
  businessUnitLimit: number | null;
  moduleKeys: string[];
  featureKeys: string[];
  prices: PlanPrice[];
  createdAt: string;
  updatedAt: string;
}

export interface PlanInput {
  displayName: string;
  description: string | null;
  active: boolean;
  businessUnitLimit: number | null;
  moduleKeys: string[];
  featureKeys: string[];
  prices: PlanPrice[];
}

/// A contratação vigente de uma empresa, com o preço congelado quando ela aceitou.
export interface Subscription {
  tenantId: string;
  tenantDisplayName: string;
  planKey: string | null;
  planDisplayName: string | null;
  currency: Currency | null;
  period: BillingPeriod | null;
  amountCents: number | null;
  businessUnitLimit: number | null;
  startedAt: string | null;
  moduleKeys: string[];
  featureKeys: string[];
}

export const billingApi = {
  modules: async (): Promise<SellableModule[]> =>
    (await api.get<{ modules: SellableModule[] }>('/root/modules')).data.modules,
  plans: async (
    filters: { search?: string | undefined; activity: Array<'ACTIVE' | 'INACTIVE'> } = {
      activity: [],
    },
  ): Promise<Plan[]> =>
    (
      await api.get<{ plans: Plan[] }>('/root/plans', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          activity: filters.activity,
        },
      })
    ).data.plans,
  createPlan: async (input: PlanInput): Promise<Plan> =>
    (await api.post<{ plan: Plan }>('/root/plans', input)).data.plan,
  updatePlan: async (key: string, input: PlanInput): Promise<Plan> =>
    (await api.put<{ plan: Plan }>(`/root/plans/${key}`, input)).data.plan,
  deletePlan: async (key: string): Promise<void> => {
    await api.delete(`/root/plans/${key}`);
  },
  subscription: async (tenantId: string): Promise<Subscription> =>
    (await api.get<{ subscription: Subscription }>(`/root/tenants/${tenantId}/subscription`)).data
      .subscription,
  applyPlan: async (
    tenantId: string,
    input: { planKey: string; currency: Currency; period: BillingPeriod },
  ): Promise<Subscription> =>
    (await api.put<{ subscription: Subscription }>(`/root/tenants/${tenantId}/subscription`, input))
      .data.subscription,
};

export const rootApi = {
  session: async (): Promise<SessionUser | null> => {
    try {
      return (await api.get<{ user: SessionUser }>('/auth/session')).data.user;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) return null;
      throw error;
    }
  },
  login: async (input: { email: string; password: string }): Promise<string | undefined> =>
    (await api.post<{ challengeToken?: string }>('/auth/root/login', input)).data.challengeToken,
  completeMfa: async (input: { challengeToken: string; code: string }): Promise<void> => {
    await api.post('/auth/root/mfa', input);
  },
  logout: async (): Promise<void> => {
    await api.post('/auth/logout');
  },
  tenants: async (filters: TenantFilters = { activity: 'ALL' }): Promise<Tenant[]> =>
    (
      await api.get<{ tenants: Tenant[] }>('/root/tenants', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          activity: filters.activity,
        },
      })
    ).data.tenants,
  createTenant: async (input: {
    displayName: string;
    legalName?: string | undefined;
    key: string;
    unitDisplayName: string;
    unitKey: string;
    timeZone: string;
  }): Promise<Tenant> => (await api.post<{ tenant: Tenant }>('/root/tenants', input)).data.tenant,
  enterTenant: async (tenantId: string, businessUnitId?: string): Promise<Tenant> =>
    (
      await api.post<{ tenant: Tenant }>(`/root/tenants/${tenantId}/enter`, {
        ...(businessUnitId ? { businessUnitId } : {}),
      })
    ).data.tenant,
  leaveTenant: async (): Promise<void> => {
    await api.delete('/root/tenant-context');
  },
  inviteOwner: async (
    tenantId: string,
    input: { firstName: string; lastName: string; email: string },
  ): Promise<{
    activationToken: string;
    invitation: { email: string; expiresAt: string };
  }> =>
    (await api.post(`/root/tenants/${tenantId}/owner-invitations`, input)).data as {
      activationToken: string;
      invitation: { email: string; expiresAt: string };
    },
};

export const ownerApi = {
  inspectInvitation: async (token: string): Promise<OwnerInvitationInspection> =>
    (await api.get<OwnerInvitationInspection>(`/auth/owner-invitations/${token}`)).data,
  activate: async (input: { token: string; password: string; code: string }): Promise<void> => {
    await api.post(`/auth/owner-invitations/${input.token}/activate`, {
      password: input.password,
      code: input.code,
    });
  },
};

export const memberInvitationApi = {
  inspectInvitation: async (token: string): Promise<OwnerInvitationInspection> =>
    (await api.get<OwnerInvitationInspection>(`/auth/member-invitations/${token}`)).data,
  activate: async (input: { token: string; password: string; code: string }): Promise<void> => {
    await api.post(`/auth/member-invitations/${input.token}/activate`, {
      password: input.password,
      code: input.code,
    });
  },
};

export const authenticatorApi = {
  beginChange: async (input: {
    password: string;
    code: string;
  }): Promise<{ changeToken: string; mfaUri: string }> =>
    (await api.post<{ changeToken: string; mfaUri: string }>('/auth/authenticator-change', input))
      .data,
  confirmChange: async (input: { changeToken: string; code: string }): Promise<void> => {
    await api.post('/auth/authenticator-change/confirm', input);
  },
};

export const accessApi = {
  resetMemberAuthenticator: async (membershipId: string, code: string): Promise<void> => {
    await api.post(`/tenant/members/${membershipId}/authenticator-reset`, { code });
  },
  members: async (filters: {
    search?: string | undefined;
    status: 'ALL' | 'INVITED' | 'ACTIVE' | 'INACTIVE';
  }): Promise<CompanyMember[]> =>
    (
      await api.get<{ members: CompanyMember[] }>('/tenant/members', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          status: filters.status,
        },
      })
    ).data.members,
  roles: async (): Promise<AccessRole[]> =>
    (await api.get<{ roles: AccessRole[] }>('/tenant/access-roles')).data.roles,
  permissions: async (): Promise<PermissionModule[]> =>
    (await api.get<{ modules: PermissionModule[] }>('/tenant/permissions')).data.modules,
  createRole: async (input: AccessRoleInput): Promise<AccessRole> =>
    (await api.post<{ role: AccessRole }>('/tenant/access-roles', input)).data.role,
  updateRole: async (roleId: string, input: AccessRoleInput): Promise<AccessRole> =>
    (await api.patch<{ role: AccessRole }>(`/tenant/access-roles/${roleId}`, input)).data.role,
  deactivateRole: async (roleId: string): Promise<void> => {
    await api.delete(`/tenant/access-roles/${roleId}`);
  },
  updateMemberUnits: async (
    membershipId: string,
    input: { unitScope: 'ALL' | 'SELECTED'; businessUnitIds: string[] },
  ): Promise<CompanyMember> =>
    (
      await api.put<{ member: CompanyMember }>(
        `/tenant/members/${membershipId}/business-units`,
        input,
      )
    ).data.member,
  updateMemberRoles: async (membershipId: string, roleIds: string[]): Promise<CompanyMember> =>
    (
      await api.put<{ member: CompanyMember }>(`/tenant/members/${membershipId}/access-roles`, {
        roleIds,
      })
    ).data.member,
  invite: async (input: {
    firstName: string;
    lastName: string;
    email: string;
    roleIds: string[];
  }): Promise<{
    activationToken: string;
    invitation: { membershipId: string; email: string; expiresAt: string };
  }> =>
    (await api.post('/tenant/member-invitations', input)).data as {
      activationToken: string;
      invitation: { membershipId: string; email: string; expiresAt: string };
    },
  create: async (input: {
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    roleIds: string[];
  }): Promise<{ member: CompanyMember }> =>
    (await api.post<{ member: CompanyMember }>('/tenant/members', input)).data,
};

export type PartnerActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type PartnerSort = 'displayName' | 'createdAt';
export type PartnerDirection = 'asc' | 'desc';

export interface PartnerPixKey {
  id: string;
  value: string;
  label: string | null;
}

export interface PartnerAccount {
  id: string;
  label: string | null;
  bankName: string | null;
  branch: string | null;
  number: string | null;
  pixKeys: PartnerPixKey[];
}

export interface Partner {
  id: string;
  displayName: string;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  accounts: PartnerAccount[];
}

export interface PartnerInput {
  displayName: string;
  notes: string | null;
  accounts: Array<{
    label: string | null;
    bankName: string | null;
    branch: string | null;
    number: string | null;
    pixKeys: Array<{ value: string; label: string | null }>;
  }>;
}

export interface PartnerFilters {
  search?: string | undefined;
  activity: PartnerActivityFilter;
  sort: PartnerSort;
  direction: PartnerDirection;
}

export const partnersApi = {
  list: async (filters: PartnerFilters): Promise<Partner[]> =>
    (
      await api.get<{ partners: Partner[] }>('/tenant/partners', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          activity: filters.activity,
          sort: filters.sort,
          direction: filters.direction,
        },
      })
    ).data.partners,
  create: async (input: PartnerInput): Promise<Partner> =>
    (await api.post<{ partner: Partner }>('/tenant/partners', input)).data.partner,
  update: async (partnerId: string, input: PartnerInput): Promise<Partner> =>
    (await api.patch<{ partner: Partner }>(`/tenant/partners/${partnerId}`, input)).data.partner,
  setActive: async (partnerId: string, active: boolean): Promise<Partner> =>
    (await api.put<{ partner: Partner }>(`/tenant/partners/${partnerId}/activation`, { active }))
      .data.partner,
};

export type ClientKind = 'PERSON';
export type ClientActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type ClientAccessFilter = 'ALL' | 'LINKED' | 'UNLINKED';
export type ClientSort = 'displayName' | 'createdAt' | 'kind';
export type ClientDirection = 'asc' | 'desc';
export type ClientAccessRelationship = 'CUSTOMER';

export interface ClientAccess {
  id: string;
  displayName: string;
  email: string;
  relationship: ClientAccessRelationship;
  status: 'INVITED' | 'ACTIVE' | 'REVOKED';
  verifiedAt: string | null;
  createdAt: string;
}

export interface Client {
  id: string;
  kind: ClientKind;
  displayName: string;
  legalName: string | null;
  document: string | null;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  notes: string | null;
  instagram: string | null;
  address: ClientAddress | null;
  emergencyContact: EmergencyContact | null;
  consents: ClientConsent[];
  referredBy: { id: string; displayName: string } | null;
  referralSource: string | null;
  referralsCount: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  accesses: ClientAccess[];
}

/// Um endereço na ficha. `country` é o código ISO; no Brasil o CEP são 8 dígitos e o estado a UF.
export interface ClientAddress {
  country: string;
  postalCode: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  city: string | null;
  state: string | null;
}

export interface PostalCodeAddress {
  street: string | null;
  district: string | null;
  city: string;
  state: string;
}

/// Quem avisar numa emergência; o telefone vem no formato internacional.
export interface EmergencyContact {
  name: string;
  relationship: string | null;
  phone: string | null;
}

export type ClientConsentKind = 'WHATSAPP_REMINDERS' | 'MARKETING' | 'IMAGE_USE';

/// O estado atual de uma autorização. `changedAt` nulo: nunca marcada.
export interface ClientConsent {
  kind: ClientConsentKind;
  granted: boolean;
  changedAt: string | null;
  changedBy: string | null;
}

export interface ClientConsentEvent {
  id: string;
  kind: ClientConsentKind;
  granted: boolean;
  actorDisplayName: string;
  occurredAt: string;
}

export interface ClientInput {
  kind: ClientKind;
  displayName: string;
  legalName: string | null;
  document: string | null;
  email: string | null;
  phone: string | null;
  birthDate: string | null;
  notes: string | null;
  instagram?: string | null;
  address?: ClientAddress | null;
  emergencyContact?: EmergencyContact | null;
  referredByClientId?: string | null;
  referralSource?: string | null;
}

export interface ClientFilters {
  cpf?: string | undefined;
  search?: string | undefined;
  activity: ClientActivityFilter | Array<Exclude<ClientActivityFilter, 'ALL'>>;
  kind: ClientKind | 'ALL';
  access: ClientAccessFilter | Array<Exclude<ClientAccessFilter, 'ALL'>>;
  /// O recorte de dias sobre a data de cadastro, lido no fuso da empresa pelo servidor.
  from?: string | undefined;
  to?: string | undefined;
  sort: ClientSort;
  direction: ClientDirection;
  limit: number;
  offset: number;
}

export const clientsApi = {
  list: async (filters: ClientFilters): Promise<{ clients: Client[]; total: number }> =>
    (await api.get<{ clients: Client[]; total: number }>('/tenant/clients', { params: filters }))
      .data,
  get: async (clientId: string): Promise<Client> =>
    (await api.get<{ client: Client }>(`/tenant/clients/${clientId}`)).data.client,
  create: async (input: ClientInput): Promise<Client> =>
    (await api.post<{ client: Client }>('/tenant/clients', input)).data.client,
  update: async (clientId: string, input: ClientInput): Promise<Client> =>
    (await api.patch<{ client: Client }>(`/tenant/clients/${clientId}`, input)).data.client,
  setActive: async (clientId: string, active: boolean): Promise<Client> =>
    (await api.put<{ client: Client }>(`/tenant/clients/${clientId}/activation`, { active })).data
      .client,
  inviteAccess: async (
    clientId: string,
    input: {
      firstName: string;
      lastName: string;
      email: string;
      relationship: ClientAccessRelationship;
    },
  ): Promise<ClientAccess> =>
    (
      await api.post<{ access: ClientAccess }>(
        `/tenant/clients/${clientId}/access-invitations`,
        input,
      )
    ).data.access,
  revokeAccess: async (clientId: string, accessId: string): Promise<void> => {
    await api.delete(`/tenant/clients/${clientId}/accesses/${accessId}`);
  },
  findPostalCode: async (postalCode: string): Promise<PostalCodeAddress> =>
    (await api.get<{ address: PostalCodeAddress }>(`/tenant/postal-codes/${postalCode}`)).data
      .address,
  setConsent: async (
    clientId: string,
    kind: ClientConsentKind,
    granted: boolean,
  ): Promise<Client> =>
    (
      await api.put<{ client: Client }>(
        `/tenant/clients/${clientId}/consents/${kind.toLowerCase().replace(/_/gu, '-')}`,
        { granted },
      )
    ).data.client,
  consentHistory: async (clientId: string): Promise<ClientConsentEvent[]> =>
    (await api.get<{ events: ClientConsentEvent[] }>(`/tenant/clients/${clientId}/consents`)).data
      .events,
};

export type ServiceActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type ServiceSort = 'displayName' | 'durationMinutes' | 'basePriceCents' | 'createdAt';
export type ServiceDirection = 'asc' | 'desc';
export interface ServiceOffering {
  id: string;
  displayName: string;
  description: string | null;
  durationMinutes: number;
  basePriceCents: number;
  currency: Currency;
  active: boolean;
  /// Preço e duração próprios por unidade (ADR 0074); nulo usa o do serviço.
  unitTerms: ServiceUnitTerms[];
  createdAt: string;
  updatedAt: string;
}

export interface ProfessionalServiceTerms {
  serviceId: string;
  priceCents: number | null;
  durationMinutes: number | null;
}

/// Os do profissional sobre os do serviço na unidade (ADR 0074, decisão 8).
export function professionalTermsFor(
  professional: Pick<SchedulingResource, 'serviceTerms'> | undefined,
  serviceId: string,
  unit: { durationMinutes: number; priceCents: number },
): { durationMinutes: number; priceCents: number } {
  const own = professional?.serviceTerms?.find((terms) => terms.serviceId === serviceId);
  return {
    durationMinutes: own?.durationMinutes ?? unit.durationMinutes,
    priceCents: own?.priceCents ?? unit.priceCents,
  };
}

export interface ServiceUnitTerms {
  businessUnitId: string;
  priceCents: number | null;
  durationMinutes: number | null;
}

/// O preço e a duração do serviço numa unidade: os dela quando informados, senão os do serviço.
export function serviceTermsFor(
  service: Pick<ServiceOffering, 'durationMinutes' | 'basePriceCents'> & {
    unitTerms?: readonly ServiceUnitTerms[] | undefined;
  },
  businessUnitId: string | null | undefined,
): { durationMinutes: number; priceCents: number } {
  const own = businessUnitId
    ? service.unitTerms?.find((terms) => terms.businessUnitId === businessUnitId)
    : undefined;
  return {
    durationMinutes: own?.durationMinutes ?? service.durationMinutes,
    priceCents: own?.priceCents ?? service.basePriceCents,
  };
}

export interface ServiceInput {
  displayName: string;
  description: string | null;
  durationMinutes: number;
  basePriceCents: number;
  currency: Currency;
  unitTerms?: ServiceUnitTerms[];
}

export interface ServiceFilters {
  search?: string | undefined;
  activity: ServiceActivityFilter;
  currency: Currency | 'ALL';
  /// O recorte de dias sobre a data de cadastro, lido no fuso da empresa pelo servidor.
  from?: string | undefined;
  to?: string | undefined;
  sort: ServiceSort;
  direction: ServiceDirection;
  limit: number;
  offset: number;
}

export const servicesApi = {
  list: async (filters: ServiceFilters): Promise<{ services: ServiceOffering[]; total: number }> =>
    (
      await api.get<{ services: ServiceOffering[]; total: number }>('/tenant/services', {
        params: filters,
      })
    ).data,
  get: async (serviceId: string): Promise<ServiceOffering> =>
    (await api.get<{ service: ServiceOffering }>(`/tenant/services/${serviceId}`)).data.service,
  create: async (input: ServiceInput): Promise<ServiceOffering> =>
    (await api.post<{ service: ServiceOffering }>('/tenant/services', input)).data.service,
  update: async (serviceId: string, input: ServiceInput): Promise<ServiceOffering> =>
    (await api.patch<{ service: ServiceOffering }>(`/tenant/services/${serviceId}`, input)).data
      .service,
  setActive: async (serviceId: string, active: boolean): Promise<ServiceOffering> =>
    (
      await api.put<{ service: ServiceOffering }>(`/tenant/services/${serviceId}/activation`, {
        active,
      })
    ).data.service,
};

export type SalesItemKind = 'SERVICE' | 'PRODUCT';
export type SalesFulfillment = 'IMMEDIATE' | 'BALANCE';
export type SaleStatus = 'CONFIRMED' | 'CANCELED';
/// O item do catálogo pelo preço que vale hoje na unidade ativa: o da promoção vigente ou, sem
/// ela, o preço-base do serviço ou da apresentação (ADR 0054).
export interface SalesCatalogItem {
  id: string;
  kind: SalesItemKind;
  displayName: string;
  /// Código de barras da apresentação; serviço não tem.
  barcode: string | null;
  basePriceCents: number;
  priceCents: number;
  promotion: SalesPromotionPrice | null;
  currency: Currency;
  active: boolean;
}
/// A promoção que dá o preço de uma linha: o bastante para mostrar até quando vale.
export interface SalesPromotionPrice {
  id: string;
  priceCents: number;
  validUntil: string | null;
}
/// Vigente, agendado e encerrado vêm das datas; arquivado só existe no pacote.
export type SalesTermStatus = 'ACTIVE' | 'SCHEDULED' | 'ENDED' | 'ARCHIVED';
export type SalesPromotionStatus = Exclude<SalesTermStatus, 'ARCHIVED'>;
export type SalesPromotionTargetKind = SalesItemKind | 'PACKAGE';
export interface SalesPackageItem {
  position: number;
  kind: SalesItemKind;
  referenceId: string;
  referenceDisplayName: string;
  quantity: number;
  fulfillment: SalesFulfillment;
  /// Preço-base vezes a quantidade, retratado ao salvar: o peso da repartição.
  allocationWeightCents: number;
}
export interface SalesPackage {
  id: string;
  displayName: string;
  description: string | null;
  priceCents: number;
  currency: Currency;
  validFrom: string;
  validUntil: string | null;
  archived: boolean;
  status: SalesTermStatus;
  businessUnitIds: string[];
  items: SalesPackageItem[];
  /// Na lista da venda, a promoção vigente na unidade ativa.
  promotion?: SalesPromotionPrice | null;
  createdAt: string;
  updatedAt: string;
}
export interface SalesPackageInput {
  displayName: string;
  description: string | null;
  priceCents: number;
  currency: Currency;
  validFrom: string;
  validUntil: string | null;
  businessUnitIds: string[];
  items: Array<{
    kind: SalesItemKind;
    referenceId: string;
    quantity: number;
    fulfillment: SalesFulfillment;
  }>;
}
export interface SalesPromotion {
  id: string;
  targetKind: SalesPromotionTargetKind;
  referenceId: string;
  targetDisplayName: string;
  priceCents: number;
  currency: Currency;
  validFrom: string;
  validUntil: string | null;
  status: SalesPromotionStatus;
  businessUnitIds: string[];
  /// O preço normal de hoje; nulo quando o alvo saiu do catálogo.
  normalPriceCents: number | null;
  createdAt: string;
  updatedAt: string;
}
export interface SalesPromotionChanges {
  priceCents: number;
  validFrom: string;
  validUntil: string | null;
  businessUnitIds: string[];
}
export interface SalesPromotionInput extends SalesPromotionChanges {
  targetKind: SalesPromotionTargetKind;
  referenceId: string;
}
export interface Sale {
  appointmentId?: string | null;
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  number: number;
  status: SaleStatus;
  clientId: string | null;
  clientDisplayName: string | null;
  grossTotalCents: number;
  discountCents: number;
  /// O que os itens cancelados com a venda ativa tiraram do total (ADR 0059).
  canceledItemsCents?: number;
  /// Cada desconto concedido, com a porcentagem quando foi digitado em % (ADR 0059).
  discounts?: Array<{ baseCents: number; amountCents: number; percentBasisPoints: number | null }>;
  netTotalCents: number;
  confirmedPaymentTotalCents: number;
  outstandingTotalCents: number;
  currency: Currency;
  soldAt: string;
  timeZone?: string;
  cancellationReason: string | null;
  items: Array<{
    id: string;
    /// O pacote vendido; nulo no item avulso.
    packageId: string | null;
    /// A promoção que deu o preço da linha.
    promotionId: string | null;
    lineDisplayName: string;
    kind?: SalesItemKind;
    referenceId?: string;
    referenceDisplayName: string;
    fulfillment?: SalesFulfillment;
    quantity: number;
    allocatedTotalCents: number;
    unitAllocationsCents: number[];
    /// De onde saiu o produto vendido para levar na hora, uma saída por lote (ADR 0059).
    deliveries?: Array<{
      locationDisplayName: string;
      lotCode: string | null;
      quantity: number;
      reversed: boolean;
    }>;
    /// O serviço que veio do agendamento, na venda do atendimento (ADR 0059).
    appointmentService?: boolean;
    /// Por que o item saiu da venda ativa: atendimento cancelado ou sessão do pacote.
    cancellation?: 'APPOINTMENT_CANCELED' | 'PACKAGE_SESSION' | null;
  }>;
  payments: SalePayment[];
}

/// Locais da unidade e lotes dos produtos da Nova venda, do que vence primeiro ao que não vence.
export interface SaleStockOptions {
  locations: Array<{ id: string; displayName: string }>;
  items: Array<{
    referenceId: string;
    requiresLot: boolean;
    /// Quantas apresentações dá para vender no local: o saldo fora dos lotes vencidos.
    available?: number;
    lots: Array<{
      id: string;
      code: string;
      expiresOn: string | null;
      balance: string;
      suggested: boolean;
      /// Quantas apresentações inteiras cabem no saldo do lote.
      packages?: number;
      /// Vencido no dia da unidade: não entra na venda.
      expired?: boolean;
    }>;
  }>;
}

/// Uma entrada da página do catálogo da Nova venda (ADR 0059): o serviço, o produto com o saldo no
/// local escolhido e onde mais há, ou o pacote vigente na unidade.
export type SalesCatalogPageEntry =
  | (SalesCatalogItem & {
      stock?: {
        available: number;
        elsewhere: { locationId: string; displayName: string; available: number } | null;
      };
    })
  | { kind: 'PACKAGE'; id: string; displayName: string; package: SalesPackage };

/// O que a Nova venda manda ao servidor: a venda nova ou os itens novos da venda do atendimento.
export interface SaleDraftInput {
  discountCents: number;
  /// Desconto digitado em porcentagem, em centésimos de ponto: 1000 é 10 %.
  discountPercentBasisPoints?: number | null;
  /// O pacote desta venda que passa a cobrir o atendimento de hoje (ADR 0059).
  useTodaySessionOfPackageId?: string;
  soldAt: string;
  items: Array<
    | {
        packageId: string;
        quantity: number;
        beneficiaryClientId?: string;
        commission?: SaleLineCommissionInput;
      }
    | {
        kind: SalesItemKind;
        referenceId: string;
        quantity: number;
        commission?: SaleLineCommissionInput;
      }
  >;
  payments: Array<{
    method: 'CASH' | 'PIX' | 'CARD' | 'OTHER' | 'CREDIT';
    status: 'PENDING' | 'AUTHORIZED' | 'CONFIRMED';
    amountCents: number;
    externalReference?: string | null;
    card?: { installmentCount: number };
  }>;
  stock?: {
    locationId: string;
    lots?: Array<{ referenceId: string; lotId: string; quantity?: number }>;
  };
}
/// `ONLINE` é a Cobrança online (ADR 0060): só nasce pelo botão Gerar cobrança.
export type SalePaymentMethod = 'CASH' | 'PIX' | 'CARD' | 'OTHER' | 'CREDIT' | 'ONLINE';
export interface SalePayment {
  card?: { installmentCount: number };
  id: string;
  method: SalePaymentMethod;
  status: 'PENDING' | 'AUTHORIZED' | 'CONFIRMED' | 'REVERSED' | 'CANCELED';
  amountCents: number;
  receivedAmountCents?: number | null;
  /// A parte devolvida pelo provedor quando a política reteve o resto (ADR 0071).
  refundedCents?: number | null;
  /// Por onde a devolução saiu quando o provedor falhou e a empresa devolveu por outro meio.
  refundMethod?: 'CASH' | 'PIX' | 'OTHER' | null;
  excessCreditReason?: string | null;
  externalReference: string | null;
  paidAt: string | null;
  /// Autoria das ações depois da venda (ADR 0053); nulo nos pagamentos lançados com ela.
  confirmedAt: string | null;
  confirmedByName: string | null;
  reversedAt: string | null;
  reversedByName: string | null;
  reversalReason: string | null;
  receipt: {
    id: string;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    scanStatus: 'CLEAN' | 'NOT_SCANNED' | 'INFECTED';
    createdAt: string;
  } | null;
}
export interface SalesSettings {
  receiptRequiredMethods: SalePaymentMethod[];
  creditAcrossUnits?: boolean;
  creditUnitOverrides?: Array<{ businessUnitId: string; creditAcrossUnits: boolean }>;
}
/// Serviço avulso vendido, como o Novo agendamento o preenche.
export interface SoldService {
  saleItemUnitId: string;
  saleId: string;
  saleNumber: number;
  businessUnitId: string;
  active: boolean;
  clientId: string | null;
  clientDisplayName: string | null;
  serviceId: string;
  serviceDisplayName: string;
  priceCents: number;
  currency: Currency;
}

/// Cada unidade de serviço avulso da venda e o horário que a usa.
export interface SaleServiceUnit {
  saleItemUnitId: string;
  serviceId: string;
  serviceDisplayName: string;
  position: number;
  quantity: number;
  appointment: { id: string; status: string; startsAt: string; timeZone: string } | null;
}

export interface CommissionRule extends CommissionRate {
  itemKind: CommissionItemKind;
  referenceId: string;
  packageMode: CommissionPackageMode | null;
  assistantBasisPoints?: number | null;
  exceptions: Array<CommissionRate & { resourceId: string }>;
}
export interface CommissionCatalogItem {
  itemKind: CommissionItemKind;
  referenceId: string;
  displayName: string;
  priceCents: number;
  rule: CommissionRule | null;
}
export interface CommissionReceiver {
  id: string;
  displayName: string;
  businessUnitId: string;
  membershipId: string | null;
  employmentBond: EmploymentBond | null;
  commissionOffPayroll: boolean;
}

export interface SaleCommissionOptions {
  enabled: boolean;
  receivers: Array<{ id: string; displayName: string }>;
  defaultResourceId: string | null;
  defaultAssistantResourceId?: string | null;
  rules: Array<{
    itemKind: CommissionItemKind;
    referenceId: string;
    packageMode: CommissionPackageMode | null;
    assistant: boolean;
    rate: CommissionRate | null;
  }>;
}
/// Quem recebe a comissão da linha vendida; nulo é "Sem comissão".
export interface SaleLineCommissionInput {
  shares: Array<{ resourceId: string | null; quantity: number }>;
  assistantResourceId?: string | null;
}

export interface CommissionClosingLine {
  resourceId: string;
  resourceDisplayName: string;
  employmentBond: EmploymentBond | null;
  offPayroll: boolean;
  accountCode: string | null;
  commissionCents: number;
  advancesCents: number;
  netCents: number;
  carryCents: number;
  /// O que ainda falta apurar, dentro do total do mês (ADR 0082, decisão 33).
  pendingCommissionCents: number;
  pendingAdvancesCents: number;
  pendingNetCents: number;
  /// O que a conciliação pagou do apurado e o que falta (ADR 0082, decisão 30).
  paidCents: number;
  outstandingCents: number;
  parts: Array<{
    id: string;
    lineDisplayName: string;
    role: 'PRINCIPAL' | 'ASSISTANT';
    quantity: number;
    amountCents: number;
  }>;
}

export interface CommissionClosingSummary {
  id: string;
  createdAt: string;
  dueOn: string;
  professionals: number;
  commissionCents: number;
  advancesCents: number;
  netCents: number;
  paidCents: number;
  outstandingCents: number;
  undoBlockedReason: string | null;
}

export interface CommissionClosingView {
  month: string;
  status: 'OPEN' | 'CLOSED';
  closings: CommissionClosingSummary[];
  canGenerateExpenses: boolean;
  monthClosed: boolean;
  lines: CommissionClosingLine[];
  totals: {
    inPayrollCents: number;
    offPayrollCents: number;
    byAccount: Array<{ accountCode: string | null; cents: number }>;
  };
}

export interface CommissionBalance {
  resourceId: string;
  resourceDisplayName: string;
  owedCents: number;
  openCents: number;
  advanceCarryCents: number;
}

export interface CommissionAdvance {
  id: string;
  resourceId: string;
  resourceDisplayName: string;
  amountCents: number;
  appliedCents: number;
  paidOn: string;
  note: string | null;
}

export type MyCommissionsView =
  | { status: 'DISABLED' | 'NO_PROFESSIONAL' }
  | {
      status: 'READY';
      month: string;
      commissionCents: number;
      advancesCents: number;
      netCents: number;
      parts: CommissionClosingLine['parts'];
    };

export interface SaleCommissionPart {
  id: string;
  lineDisplayName: string;
  role: 'PRINCIPAL' | 'ASSISTANT';
  resourceId: string;
  resourceDisplayName: string;
  quantity: number;
  amountCents: number;
  status: 'PENDING' | 'ACCRUED' | 'VOIDED';
  closed: boolean;
}

export const commissionsApi = {
  closing: async (month: string) =>
    (
      await api.get<{ closing: CommissionClosingView }>('/tenant/sales/commissions/closing', {
        params: { month },
      })
    ).data.closing,
  undoClosing: async (closingId: string) => {
    await api.delete(`/tenant/sales/commissions/closing/${closingId}`);
  },
  balances: async () =>
    (await api.get<{ balances: CommissionBalance[] }>('/tenant/sales/commissions/balances')).data
      .balances,
  closeMonth: async (input: { month: string; dueOn: string }) => {
    await api.post('/tenant/sales/commissions/closing', input);
  },
  advances: async () =>
    (await api.get<{ advances: CommissionAdvance[] }>('/tenant/sales/commissions/advances')).data
      .advances,
  createAdvance: async (input: {
    resourceId: string;
    amountCents: number;
    paidOn: string;
    note: string | null;
  }) => {
    await api.post('/tenant/sales/commissions/advances', input);
  },
  removeAdvance: async (advanceId: string) => {
    await api.delete(`/tenant/sales/commissions/advances/${advanceId}`);
  },
  unitSettings: async () =>
    (
      await api.get<{ settings: { professionalsSeeOwn: boolean } }>(
        '/tenant/sales/commissions/unit-settings',
      )
    ).data.settings,
  saveUnitSettings: async (professionalsSeeOwn: boolean) => {
    await api.put('/tenant/sales/commissions/unit-settings', { professionalsSeeOwn });
  },
  mine: async (month: string) =>
    (
      await api.get<{ mine: MyCommissionsView }>('/tenant/sales/commissions/mine', {
        params: { month },
      })
    ).data.mine,
  saleParts: async (saleId: string) =>
    (await api.get<{ parts: SaleCommissionPart[] }>(`/tenant/sales/${saleId}/commissions`)).data
      .parts,
  reassignPart: async (partId: string, input: { resourceId: string | null; reason: string }) => {
    await api.post(`/tenant/sales/commissions/parts/${partId}/reassign`, input);
  },
  saleOptions: async (params: {
    fromAppointment?: boolean;
    appointmentProfessionalId?: string;
    appointmentAssistantId?: string;
  }): Promise<SaleCommissionOptions> =>
    (
      await api.get<{ options: SaleCommissionOptions }>('/tenant/sales/commissions/sale-options', {
        params,
      })
    ).data.options,
  catalog: async (params: {
    itemKind: CommissionItemKind;
    search?: string | undefined;
    limit: number;
    offset: number;
  }) =>
    (
      await api.get<{ items: CommissionCatalogItem[]; total: number }>(
        '/tenant/sales/commissions/catalog',
        { params },
      )
    ).data,
  receivers: async (): Promise<CommissionReceiver[]> =>
    (await api.get<{ receivers: CommissionReceiver[] }>('/tenant/sales/commissions/receivers')).data
      .receivers,
  saveRule: async (
    itemKind: CommissionItemKind,
    referenceId: string,
    input: Pick<
      CommissionRule,
      'rateType' | 'rateValue' | 'packageMode' | 'exceptions' | 'assistantBasisPoints'
    >,
  ): Promise<CommissionRule> =>
    (
      await api.put<{ rule: CommissionRule }>(
        `/tenant/sales/commissions/rules/${itemKind}/${referenceId}`,
        input,
      )
    ).data.rule,
  removeRule: async (itemKind: CommissionItemKind, referenceId: string): Promise<void> => {
    await api.delete(`/tenant/sales/commissions/rules/${itemKind}/${referenceId}`);
  },
};

export const salesApi = {
  get: async (saleId: string): Promise<Sale> =>
    (await api.get<{ sale: Sale }>(`/tenant/sales/${saleId}`)).data.sale,
  services: async (saleId: string): Promise<SaleServiceUnit[]> =>
    (await api.get<{ services: SaleServiceUnit[] }>(`/tenant/sales/${saleId}/services`)).data
      .services,
  async clientCredit(clientId: string, businessUnitId?: string) {
    return (
      await api.get<{
        balances: Array<{ currency: Currency; totalCents: number; availableCents: number }>;
      }>(`/tenant/sales/clients/${clientId}/credit`, { params: { businessUnitId } })
    ).data;
  },
  async credit(saleId: string) {
    return (
      await api.get<{ credit: { availableCents: number; currency: Currency } }>(
        `/tenant/sales/${saleId}/credit`,
      )
    ).data.credit;
  },
  appointmentSale: async (appointmentId: string): Promise<Sale | null> =>
    (await api.get<{ sale: Sale | null }>(`/tenant/sales/appointments/${appointmentId}`)).data.sale,
  /// O catálogo da Nova venda em páginas, com o saldo dos produtos no local (ADR 0059).
  catalogPage: async (query: {
    kind: 'ALL' | 'SERVICE' | 'PRODUCT' | 'PACKAGE';
    search?: string;
    locationId?: string;
    limit: number;
    offset: number;
  }): Promise<{ items: SalesCatalogPageEntry[]; total: number }> =>
    (
      await api.get<{ items: SalesCatalogPageEntry[]; total: number }>(
        '/tenant/sales/catalog/page',
        { params: query },
      )
    ).data,
  /// Itens novos na venda do atendimento ainda ativa (ADR 0059).
  addSaleItems: async (saleId: string, input: SaleDraftInput): Promise<Sale> =>
    (await api.post<{ sale: Sale }>(`/tenant/sales/${saleId}/items`, input)).data.sale,

  catalog: async (
    search?: string,
  ): Promise<{ services: SalesCatalogItem[]; products: SalesCatalogItem[] }> =>
    (
      await api.get<{ services: SalesCatalogItem[]; products: SalesCatalogItem[] }>(
        '/tenant/sales/catalog',
        { params: search ? { search } : {} },
      )
    ).data,
  /// Os quatro itens que abrem a venda: os mais vendidos, completados em ordem alfabética.
  featured: async (): Promise<{ items: SalesCatalogItem[] }> =>
    (await api.get<{ items: SalesCatalogItem[] }>('/tenant/sales/catalog/featured')).data,
  /// `forSale` é a lista da Nova venda: os vigentes da unidade ativa, com a promoção dela.
  packages: async (filters: {
    search?: string;
    status: SalesTermStatus;
    forSale?: boolean;
    sort?: 'name' | 'date' | 'price' | undefined;
    direction?: 'asc' | 'desc' | undefined;
    limit: number;
    offset: number;
  }): Promise<{
    packages: SalesPackage[];
    total: number;
    counts: Record<SalesTermStatus, number>;
  }> =>
    (
      await api.get<{
        packages: SalesPackage[];
        total: number;
        counts: Record<SalesTermStatus, number>;
      }>('/tenant/sales/packages', { params: filters })
    ).data,
  package: async (id: string): Promise<SalesPackage> =>
    (await api.get<{ package: SalesPackage }>(`/tenant/sales/packages/${id}`)).data.package,
  createPackage: async (input: SalesPackageInput): Promise<SalesPackage> =>
    (await api.post<{ package: SalesPackage }>('/tenant/sales/packages', input)).data.package,
  updatePackage: async (id: string, input: SalesPackageInput): Promise<SalesPackage> =>
    (await api.patch<{ package: SalesPackage }>(`/tenant/sales/packages/${id}`, input)).data
      .package,
  setPackageArchived: async (id: string, archived: boolean): Promise<SalesPackage> =>
    (
      await api.put<{ package: SalesPackage }>(`/tenant/sales/packages/${id}/archive`, {
        archived,
      })
    ).data.package,
  promotions: async (filters: {
    search?: string;
    status: SalesPromotionStatus;
    sort?: 'name' | 'date' | 'price' | undefined;
    direction?: 'asc' | 'desc' | undefined;
    limit: number;
    offset: number;
  }): Promise<{
    promotions: SalesPromotion[];
    total: number;
    counts: Record<SalesPromotionStatus, number>;
  }> =>
    (
      await api.get<{
        promotions: SalesPromotion[];
        total: number;
        counts: Record<SalesPromotionStatus, number>;
      }>('/tenant/sales/promotions', { params: filters })
    ).data,
  createPromotion: async (input: SalesPromotionInput): Promise<SalesPromotion> =>
    (await api.post<{ promotion: SalesPromotion }>('/tenant/sales/promotions', input)).data
      .promotion,
  updatePromotion: async (id: string, input: SalesPromotionChanges): Promise<SalesPromotion> =>
    (await api.patch<{ promotion: SalesPromotion }>(`/tenant/sales/promotions/${id}`, input)).data
      .promotion,
  endPromotion: async (id: string): Promise<SalesPromotion> =>
    (await api.post<{ promotion: SalesPromotion }>(`/tenant/sales/promotions/${id}/end`)).data
      .promotion,
  sales: async (filters: {
    search?: string;
    status: 'ALL' | SaleStatus;
    method?: 'ALL' | 'CASH' | 'PIX' | 'CARD' | 'OTHER' | 'CREDIT';
    payment?: 'ALL' | 'OUTSTANDING' | 'AWAITING';
    from?: string;
    to?: string;
    sort?: 'number' | 'date' | 'client' | 'unit' | 'total' | undefined;
    direction?: 'asc' | 'desc' | undefined;
    limit: number;
    offset: number;
  }): Promise<{ sales: Sale[]; total: number }> =>
    (await api.get<{ sales: Sale[]; total: number }>('/tenant/sales', { params: filters })).data,
  createSale: async (
    input: SaleDraftInput & { clientId?: string | null; appointmentId?: string },
  ): Promise<Sale> => (await api.post<{ sale: Sale }>('/tenant/sales', input)).data.sale,
  stockOptions: async (query: {
    locationId?: string | undefined;
    items: Array<{ referenceId: string; quantity: number }>;
  }): Promise<SaleStockOptions> =>
    (
      await api.get<SaleStockOptions>('/tenant/sales/stock-options', {
        params: {
          ...(query.locationId ? { locationId: query.locationId } : {}),
          items: query.items
            .map((item) => `${item.referenceId}:${String(item.quantity)}`)
            .join(','),
        },
      })
    ).data,
  cancelSale: async (id: string, reason: string): Promise<Sale> =>
    (await api.post<{ sale: Sale }>(`/tenant/sales/${id}/cancel`, { reason })).data.sale,
  /// O comprovante sobe em base64 no corpo JSON; quem decide o formato é o conteúdo, no servidor.
  registerPayment: async (
    saleId: string,
    input: {
      method: SalePaymentMethod;
      amountCents: number;
      paidOn: string;
      excessCreditReason?: string | undefined;
      externalReference?: string | null;
      card?: { installmentCount: number };
      receipt?: File | null;
    },
  ): Promise<Sale> => {
    const { receipt, ...body } = input;
    return (
      await api.post<{ sale: Sale }>(`/tenant/sales/${saleId}/payments`, {
        ...body,
        ...(receipt ? { receipt: await receiptBody(receipt) } : {}),
      })
    ).data.sale;
  },
  confirmPayment: async (
    saleId: string,
    paymentId: string,
    input: { amountCents: number; paidOn: string; receipt?: File | null },
  ): Promise<Sale> => {
    const { receipt, ...body } = input;
    return (
      await api.post<{ sale: Sale }>(`/tenant/sales/${saleId}/payments/${paymentId}/confirm`, {
        ...body,
        ...(receipt ? { receipt: await receiptBody(receipt) } : {}),
      })
    ).data.sale;
  },
  reversePayment: async (
    saleId: string,
    paymentId: string,
    reason: string,
    reversalKind?: 'CORRECTION' | 'ECONOMIC',
  ): Promise<Sale> =>
    (
      await api.post<{ sale: Sale }>(`/tenant/sales/${saleId}/payments/${paymentId}/reverse`, {
        reason,
        ...(reversalKind ? { reversalKind } : {}),
      })
    ).data.sale,
  attachPaymentReceipt: async (saleId: string, paymentId: string, file: File): Promise<Sale> =>
    (
      await api.post<{ sale: Sale }>(
        `/tenant/sales/${saleId}/payments/${paymentId}/receipt`,
        await receiptBody(file),
      )
    ).data.sale,
  /// A URL do arquivo; o acesso é resolvido no servidor pela linha do comprovante.
  paymentReceiptUrl: (receiptId: string): string =>
    `${api.defaults.baseURL ?? ''}/tenant/sales/payment-receipts/${receiptId}/file`,
  settings: async (): Promise<SalesSettings> =>
    (await api.get<{ settings: SalesSettings }>('/tenant/sales/settings')).data.settings,
  updateSettings: async (settings: SalesSettings): Promise<SalesSettings> =>
    (await api.put<{ settings: SalesSettings }>('/tenant/sales/settings', settings)).data.settings,
};

export type OnlinePaymentEnvironment = 'SIMULATED' | 'ASAAS_SANDBOX' | 'ASAAS_PRODUCTION';
export interface OnlinePaymentAccount {
  id: string;
  businessUnitId: string | null;
  environment: OnlinePaymentEnvironment;
  apiKeyLast4: string | null;
  accountName: string;
  accountDocumentMasked: string | null;
  dueDays: number;
  active: boolean;
  connectedAt: string;
  lastNoticeAt: string | null;
  noticeUrl: string | null;
  noticeToken: string | null;
}
export type OnlineChargeStatus =
  | 'CREATING'
  | 'WAITING'
  | 'PAID'
  | 'CANCELED'
  | 'EXPIRED'
  | 'FAILED'
  | 'REFUNDED'
  | 'PAID_AFTER_CLOSE';
export interface OnlineCharge {
  id: string;
  saleId: string;
  salePaymentId: string;
  environment: OnlinePaymentEnvironment;
  amountCents: number;
  dueOn: string;
  description: string;
  status: OnlineChargeStatus;
  invoiceUrl: string | null;
  pixPayload: string | null;
  pixQrImage: string | null;
  paidWith: 'PIX' | 'BOLETO' | 'CARD' | 'OTHER' | null;
  netValueCents: number | null;
  paidOn: string | null;
  closedReason: string | null;
  /// AUTHORIZE: o provedor segura a devolução até a empresa aprová-la no painel dele.
  /// FAILED: falhou no provedor e espera a empresa; MANUAL: a empresa devolveu por outro meio.
  refundStatus?: 'PENDING' | 'DONE' | 'CANCELLED' | 'AUTHORIZE' | 'FAILED' | 'MANUAL' | null;
  createdByName: string;
  createdAt: string;
}
export type SimulatedChargeOutcome = 'PAID_PIX' | 'PAID_CARD' | 'OVERDUE' | 'REFUNDED';

export interface OnlinePaymentsSettings {
  environments: OnlinePaymentEnvironment[];
  accounts: OnlinePaymentAccount[];
}
export interface OnlinePaymentsStatus {
  available: boolean;
  contracted: boolean;
  environment: OnlinePaymentEnvironment | null;
  dueDays: number | null;
}
/// Cobrança online (ADR 0060): a conta do provedor e a cobrança de cada venda.
export const onlinePaymentsApi = {
  settings: async (): Promise<OnlinePaymentsSettings> =>
    (await api.get<OnlinePaymentsSettings>('/tenant/online-payments/settings')).data,
  status: async (): Promise<OnlinePaymentsStatus> =>
    (await api.get<OnlinePaymentsStatus>('/tenant/online-payments/status')).data,
  connect: async (input: {
    businessUnitId: string | null;
    environment: OnlinePaymentEnvironment;
    apiKey?: string | null;
    dueDays?: number;
  }): Promise<OnlinePaymentAccount> =>
    (await api.post<{ account: OnlinePaymentAccount }>('/tenant/online-payments/accounts', input))
      .data.account,
  replaceKey: async (accountId: string, apiKey: string): Promise<OnlinePaymentAccount> =>
    (
      await api.put<{ account: OnlinePaymentAccount }>(
        `/tenant/online-payments/accounts/${accountId}/key`,
        { apiKey },
      )
    ).data.account,
  updateDueDays: async (accountId: string, dueDays: number): Promise<OnlinePaymentAccount> =>
    (
      await api.put<{ account: OnlinePaymentAccount }>(
        `/tenant/online-payments/accounts/${accountId}/due-days`,
        { dueDays },
      )
    ).data.account,
  disconnect: async (accountId: string): Promise<void> => {
    await api.delete(`/tenant/online-payments/accounts/${accountId}`);
  },
  charges: async (saleId: string): Promise<OnlineCharge[]> =>
    (await api.get<{ charges: OnlineCharge[] }>(`/tenant/sales/${saleId}/online-charges`)).data
      .charges,
  create: async (
    saleId: string,
    input: { amountCents: number; dueOn?: string; description?: string },
  ): Promise<OnlineCharge> =>
    (await api.post<{ charge: OnlineCharge }>(`/tenant/sales/${saleId}/online-charges`, input)).data
      .charge,
  cancel: async (chargeId: string): Promise<OnlineCharge> =>
    (await api.post<{ charge: OnlineCharge }>(`/tenant/online-charges/${chargeId}/cancel`)).data
      .charge,
  refresh: async (chargeId: string): Promise<OnlineCharge> =>
    (await api.post<{ charge: OnlineCharge }>(`/tenant/online-charges/${chargeId}/refresh`)).data
      .charge,
  whatsapp: async (chargeId: string): Promise<{ whatsappUrl: string | null }> =>
    (await api.post<{ whatsappUrl: string | null }>(`/tenant/online-charges/${chargeId}/whatsapp`))
      .data,
  simulate: async (chargeId: string, outcome: SimulatedChargeOutcome): Promise<OnlineCharge> =>
    (
      await api.post<{ charge: OnlineCharge }>(`/tenant/online-charges/${chargeId}/simulate`, {
        outcome,
      })
    ).data.charge,
};

async function receiptBody(file: File): Promise<{ fileName: string; content: string }> {
  return { fileName: file.name, content: await fileToBase64(file) };
}

export type InventoryBaseUnit = 'UNIT' | 'MILLILITER' | 'GRAM';
export type InventoryLotTracking = 'NONE' | 'REQUIRED';
export type InventoryActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type InventoryLotTrackingFilter = 'ALL' | InventoryLotTracking;
export type InventoryProductSort = 'displayName' | 'sku' | 'createdAt';
export type InventoryDirection = 'asc' | 'desc';

export interface InventoryPackage {
  id: string;
  displayName: string;
  baseQuantity: string;
  /// Preço-base de venda; nulo é apresentação que não se vende (ADR 0048).
  salePriceCents: number | null;
  /// Código de barras da embalagem, único na empresa.
  barcode: string | null;
  active: boolean;
}
export interface InventoryProduct {
  id: string;
  displayName: string;
  description: string | null;
  sku: string | null;
  baseUnit: InventoryBaseUnit;
  lotTracking: InventoryLotTracking;
  /// Vendável é declaração do cadastro; sem ela o produto é insumo e não aparece em Vendas.
  sellable: boolean;
  active: boolean;
  packages: InventoryPackage[];
  createdAt: string;
  updatedAt: string;
}
export interface InventoryPackageInput {
  id?: string | null;
  displayName: string;
  baseQuantity: string;
  salePriceCents?: number | null;
  barcode?: string | null;
}
export interface InventoryProductRevisionInput {
  displayName: string;
  description: string | null;
  sku: string | null;
  lotTracking: InventoryLotTracking;
  sellable: boolean;
  packages: InventoryPackageInput[];
}
export interface InventoryProductInput extends InventoryProductRevisionInput {
  baseUnit: InventoryBaseUnit;
}
export interface InventoryLocation {
  id: string;
  /// A unidade onde o local fica (ADR 0039).
  businessUnitId: string;
  businessUnitName: string;
  displayName: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface InventoryProductFilters {
  search?: string | undefined;
  activity: InventoryActivityFilter;
  lotTracking: InventoryLotTrackingFilter;
  /// O recorte de dias sobre a data de cadastro, lido no fuso da empresa pelo servidor.
  from?: string | undefined;
  to?: string | undefined;
  sort: InventoryProductSort;
  direction: InventoryDirection;
  limit: number;
  offset: number;
}

export const inventoryApi = {
  listProducts: async (
    filters: InventoryProductFilters,
  ): Promise<{ products: InventoryProduct[]; total: number }> =>
    (
      await api.get<{ products: InventoryProduct[]; total: number }>('/tenant/inventory/products', {
        params: filters,
      })
    ).data,
  getProduct: async (productId: string): Promise<InventoryProduct> =>
    (await api.get<{ product: InventoryProduct }>(`/tenant/inventory/products/${productId}`)).data
      .product,
  createProduct: async (input: InventoryProductInput): Promise<InventoryProduct> =>
    (await api.post<{ product: InventoryProduct }>('/tenant/inventory/products', input)).data
      .product,
  updateProduct: async (
    productId: string,
    input: InventoryProductRevisionInput,
  ): Promise<InventoryProduct> =>
    (
      await api.patch<{ product: InventoryProduct }>(
        `/tenant/inventory/products/${productId}`,
        input,
      )
    ).data.product,
  setProductActive: async (productId: string, active: boolean): Promise<InventoryProduct> =>
    (
      await api.put<{ product: InventoryProduct }>(
        `/tenant/inventory/products/${productId}/activation`,
        { active },
      )
    ).data.product,
  /// `REACHABLE` traz os locais de todas as unidades que a pessoa alcança — o destino de uma
  /// transferência. Sem pedir, vêm os da unidade da sessão.
  listLocations: async (
    activity: InventoryActivityFilter = 'ALL',
    reach: 'SESSION' | 'REACHABLE' = 'SESSION',
  ): Promise<InventoryLocation[]> =>
    (
      await api.get<{ locations: InventoryLocation[] }>('/tenant/inventory/locations', {
        params: { activity, ...(reach === 'REACHABLE' ? { reach } : {}) },
      })
    ).data.locations,
  /// O local nasce na unidade da sessão.
  createLocation: async (input: {
    displayName: string;
    businessUnitId?: string | undefined;
  }): Promise<InventoryLocation> =>
    (await api.post<{ location: InventoryLocation }>('/tenant/inventory/locations', input)).data
      .location,

  renameLocation: async (
    locationId: string,
    input: { displayName: string; businessUnitId: string },
  ): Promise<InventoryLocation> =>
    (
      await api.patch<{ location: InventoryLocation }>(
        `/tenant/inventory/locations/${locationId}`,
        input,
      )
    ).data.location,
};

export type SupplierKind = 'PERSON' | 'COMPANY';
export type SupplierOfferingKind = 'PRODUCT' | 'SERVICE';
export type PurchasingActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type PurchasingKindFilter = 'ALL' | SupplierKind;
export type PurchasingOfferingKindFilter = 'ALL' | SupplierOfferingKind;
export type SupplierSort = 'displayName' | 'createdAt';
export type SupplierOfferingSort = 'displayName' | 'supplierDisplayName' | 'unitPriceCents';
export type PurchasingDirection = 'asc' | 'desc';

export interface Supplier {
  id: string;
  kind: SupplierKind;
  displayName: string;
  legalName: string | null;
  document: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface SupplierInput {
  kind: SupplierKind;
  displayName: string;
  legalName: string | null;
  document: string | null;
  email: string | null;
  phone: string | null;
  notes: string | null;
}
export interface SupplierOffering {
  id: string;
  supplierId: string;
  supplierDisplayName: string;
  supplierActive: boolean;
  kind: SupplierOfferingKind;
  productId: string | null;
  packageId: string | null;
  productDisplayName: string | null;
  packageDisplayName: string | null;
  packageBaseQuantity: string | null;
  displayName: string;
  supplierCode: string | null;
  unitPriceCents: number;
  currency: Currency;
  minimumQuantity: number | null;
  leadTimeDays: number | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface SupplierOfferingInput {
  kind: SupplierOfferingKind;
  productId: string | null;
  packageId: string | null;
  displayName: string;
  supplierCode: string | null;
  unitPriceCents: number;
  currency: Currency;
  minimumQuantity: number | null;
  leadTimeDays: number | null;
  notes: string | null;
}
export interface PurchasableProduct {
  id: string;
  displayName: string;
  sku: string | null;
  baseUnit: InventoryBaseUnit;
  active: boolean;
  packages: InventoryPackage[];
}
export type PurchaseOrderStatus = 'OPEN' | 'COMPLETED' | 'CANCELED';
export interface PurchaseLine {
  id: string;
  offeringId: string;
  purchaseOrderItemId: string | null;
  kind: SupplierOfferingKind;
  displayName: string;
  productId: string | null;
  packageId: string | null;
  productDisplayName: string | null;
  packageDisplayName: string | null;
  packageBaseQuantity: string | null;
  quantity: string;
  receivedQuantity: string;
  unitPriceCents: number;
  currency: Currency;
  inventoryMovementId: string | null;
}
export interface PurchaseOrder {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  number: number;
  supplierId: string;
  supplierDisplayName: string;
  status: PurchaseOrderStatus;
  orderedOn: string;
  expectedOn: string | null;
  notes: string | null;
  items: PurchaseLine[];
  createdAt: string;
}
export interface GoodsReceipt {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  number: number;
  supplierId: string;
  supplierDisplayName: string;
  purchaseOrderId: string | null;
  idempotencyKey: string;
  receivedOn: string;
  dueOn: string;
  invoiceNumber: string | null;
  notes: string | null;
  items: PurchaseLine[];
  totalCents: number;
  currency: Currency;
  createdAt: string;
}
export interface PurchaseItemInput {
  offeringId: string;
  quantity: string;
  unitPriceCents: number;
  purchaseOrderItemId?: string | null;
}
export interface SupplierFilters {
  search?: string | undefined;
  activity: PurchasingActivityFilter;
  kind: PurchasingKindFilter;
  /// O recorte de dias sobre a data de cadastro, lido no fuso da empresa pelo servidor.
  from?: string | undefined;
  to?: string | undefined;
  sort: SupplierSort;
  direction: PurchasingDirection;
  limit: number;
  offset: number;
}
export interface SupplierOfferingFilters {
  search?: string | undefined;
  activity: PurchasingActivityFilter;
  kind: PurchasingOfferingKindFilter;
  supplierId?: string | undefined;
  productId?: string | undefined;
  sort: SupplierOfferingSort;
  direction: PurchasingDirection;
  limit: number;
  offset: number;
}

export const purchasingApi = {
  listSuppliers: async (
    filters: SupplierFilters,
  ): Promise<{ suppliers: Supplier[]; total: number }> =>
    (
      await api.get<{ suppliers: Supplier[]; total: number }>('/tenant/purchasing/suppliers', {
        params: filters,
      })
    ).data,
  getSupplier: async (supplierId: string): Promise<Supplier> =>
    (await api.get<{ supplier: Supplier }>(`/tenant/purchasing/suppliers/${supplierId}`)).data
      .supplier,
  createSupplier: async (input: SupplierInput): Promise<Supplier> =>
    (await api.post<{ supplier: Supplier }>('/tenant/purchasing/suppliers', input)).data.supplier,
  updateSupplier: async (supplierId: string, input: SupplierInput): Promise<Supplier> =>
    (await api.patch<{ supplier: Supplier }>(`/tenant/purchasing/suppliers/${supplierId}`, input))
      .data.supplier,
  setSupplierActive: async (supplierId: string, active: boolean): Promise<Supplier> =>
    (
      await api.put<{ supplier: Supplier }>(
        `/tenant/purchasing/suppliers/${supplierId}/activation`,
        { active },
      )
    ).data.supplier,
  listOrders: async (params: {
    search?: string;
    supplierId?: string;
    status?: PurchaseOrderStatus;
    limit: number;
    offset: number;
  }): Promise<{ orders: PurchaseOrder[]; total: number }> =>
    (
      await api.get<{ orders: PurchaseOrder[]; total: number }>('/tenant/purchasing/orders', {
        params,
      })
    ).data,
  createOrder: async (input: {
    supplierId: string;
    orderedOn: string;
    expectedOn: string | null;
    notes: string | null;
    items: PurchaseItemInput[];
  }): Promise<PurchaseOrder> =>
    (await api.post<{ order: PurchaseOrder }>('/tenant/purchasing/orders', input)).data.order,
  cancelOrder: async (orderId: string): Promise<PurchaseOrder> =>
    (await api.post<{ order: PurchaseOrder }>(`/tenant/purchasing/orders/${orderId}/cancel`)).data
      .order,
  listReceipts: async (params: {
    search?: string;
    supplierId?: string;
    limit: number;
    offset: number;
  }): Promise<{ receipts: GoodsReceipt[]; total: number }> =>
    (
      await api.get<{ receipts: GoodsReceipt[]; total: number }>('/tenant/purchasing/receipts', {
        params,
      })
    ).data,
  createReceipt: async (input: {
    idempotencyKey: string;
    supplierId: string;
    purchaseOrderId: string | null;
    locationId: string | null;
    receivedOn: string;
    dueOn: string;
    invoiceNumber: string | null;
    notes: string | null;
    items: PurchaseItemInput[];
  }): Promise<GoodsReceipt> =>
    (await api.post<{ receipt: GoodsReceipt }>('/tenant/purchasing/receipts', input)).data.receipt,
  listPurchasableProducts: async (
    search?: string,
  ): Promise<{ available: boolean; products: PurchasableProduct[] }> =>
    (
      await api.get<{ available: boolean; products: PurchasableProduct[] }>(
        '/tenant/purchasing/purchasable-products',
        {
          params: { ...(search ? { search } : {}), limit: 100 },
        },
      )
    ).data,
  listOfferings: async (
    filters: SupplierOfferingFilters,
  ): Promise<{ offerings: SupplierOffering[]; total: number }> =>
    (
      await api.get<{ offerings: SupplierOffering[]; total: number }>(
        '/tenant/purchasing/offerings',
        { params: filters },
      )
    ).data,
  getOffering: async (offeringId: string): Promise<SupplierOffering> =>
    (await api.get<{ offering: SupplierOffering }>(`/tenant/purchasing/offerings/${offeringId}`))
      .data.offering,
  createOffering: async (
    input: SupplierOfferingInput & { supplierId: string },
  ): Promise<SupplierOffering> =>
    (await api.post<{ offering: SupplierOffering }>('/tenant/purchasing/offerings', input)).data
      .offering,
  updateOffering: async (
    offeringId: string,
    input: SupplierOfferingInput,
  ): Promise<SupplierOffering> =>
    (
      await api.patch<{ offering: SupplierOffering }>(
        `/tenant/purchasing/offerings/${offeringId}`,
        input,
      )
    ).data.offering,
  setOfferingActive: async (offeringId: string, active: boolean): Promise<SupplierOffering> =>
    (
      await api.put<{ offering: SupplierOffering }>(
        `/tenant/purchasing/offerings/${offeringId}/activation`,
        { active },
      )
    ).data.offering,
};

export type InventoryMovementKind = 'ENTRY' | 'CONSUMPTION' | 'LOSS' | 'ADJUSTMENT' | 'TRANSFER';
export type InventoryMovementFlow = 'IN' | 'OUT';
export type InventoryLossReason = 'EXPIRATION' | 'BREAKAGE' | 'THEFT' | 'INTERNAL_USE';
export type InventoryMovementSort = 'occurredAt' | 'quantity';
export type InventoryAlert = 'NONE' | 'REORDER' | 'BELOW_MINIMUM';

export interface InventoryReference {
  id: string;
  displayName: string;
}
/// O local e a unidade onde ele fica: na visão consolidada, é o que diz de qual unidade é a linha.
export interface InventoryLocationReference extends InventoryReference {
  businessUnit: InventoryReference;
}
export interface InventoryLotReference {
  id: string;
  code: string;
  expiresOn: string | null;
}
export interface InventoryMovement {
  id: string;
  kind: InventoryMovementKind;
  direction: InventoryMovementFlow;
  quantity: string;
  occurredAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  product: InventoryReference & { baseUnit: InventoryBaseUnit };
  location: InventoryLocationReference;
  lot: InventoryLotReference | null;
  package: InventoryReference | null;
  packageQuantity: string | null;
  lossReason: InventoryLossReason | null;
  countedQuantity: string | null;
  note: string | null;
  reversesMovementId: string | null;
  reversedByMovementId: string | null;
  transferGroupId: string | null;
  createdAt: string;
}
export interface InventoryBalanceRow {
  product: InventoryReference & { baseUnit: InventoryBaseUnit; sku: string | null };
  location: InventoryLocationReference;
  lot: InventoryLotReference | null;
  quantity: string;
  minimumQuantity: string | null;
  reorderPoint: string | null;
  alert: InventoryAlert;
}
export interface InventoryReplenishmentPolicy {
  id: string;
  product: InventoryReference;
  location: InventoryLocationReference;
  minimumQuantity: string;
  reorderPoint: string;
}
export interface InventoryLotSuggestion {
  lot: InventoryLotReference;
  balance: string;
  suggested: boolean;
}
/// Quantidade sempre em texto: decimal exato não atravessa a borda como número binário.
export type InventoryQuantityInput =
  { baseQuantity: string } | { packageId: string; packageQuantity: string };

export interface InventoryMovementFilters {
  search?: string | undefined;
  productId?: string | undefined;
  locationId?: string | undefined;
  kind: 'ALL' | InventoryMovementKind;
  flow: 'ALL' | InventoryMovementFlow;
  from?: string | undefined;
  to?: string | undefined;
  sort: InventoryMovementSort;
  sortDirection: 'asc' | 'desc';
  limit: number;
  offset: number;
}
export interface InventoryBalanceFilters {
  search?: string | undefined;
  productId?: string | undefined;
  locationId?: string | undefined;
  activity: InventoryActivityFilter;
  alertOnly: boolean;
  limit: number;
  offset: number;
}

export const inventoryMovementsApi = {
  list: async (
    filters: InventoryMovementFilters,
  ): Promise<{ movements: InventoryMovement[]; total: number }> =>
    (
      await api.get<{ movements: InventoryMovement[]; total: number }>(
        '/tenant/inventory/movements',
        { params: filters },
      )
    ).data,
  balances: async (
    filters: InventoryBalanceFilters,
  ): Promise<{ rows: InventoryBalanceRow[]; total: number }> =>
    (
      await api.get<{ rows: InventoryBalanceRow[]; total: number }>('/tenant/inventory/balances', {
        params: filters,
      })
    ).data,
  suggestLots: async (query: {
    productId: string;
    locationId: string;
    quantity: string;
  }): Promise<InventoryLotSuggestion[]> =>
    (
      await api.get<{ lots: InventoryLotSuggestion[] }>('/tenant/inventory/lot-suggestions', {
        params: query,
      })
    ).data.lots,
  register: async (input: {
    kind: 'ENTRY' | 'CONSUMPTION' | 'LOSS';
    productId: string;
    locationId?: string;
    quantity: InventoryQuantityInput;
    lot?: { id: string } | { code: string; expiresOn: string | null };
    lossReason?: InventoryLossReason | null;
    note?: string | null;
    occurredOn?: string;
  }): Promise<InventoryMovement> =>
    (await api.post<{ movement: InventoryMovement }>('/tenant/inventory/movements', input)).data
      .movement,
  /// Devolve nulo quando a contagem bate com o saldo: não havia o que ajustar.
  adjust: async (input: {
    productId: string;
    locationId: string;
    lotId?: string | null;
    countedQuantity: string;
    note: string;
    occurredOn?: string;
  }): Promise<InventoryMovement | null> =>
    (
      await api.post<{ movement: InventoryMovement | null }>(
        '/tenant/inventory/movements/adjustment',
        input,
      )
    ).data.movement,
  transfer: async (input: {
    productId: string;
    originLocationId: string;
    destinationLocationId: string;
    quantity: InventoryQuantityInput;
    lotId?: string | null;
    note?: string | null;
    occurredOn?: string;
  }): Promise<InventoryMovement[]> =>
    (
      await api.post<{ movements: InventoryMovement[] }>(
        '/tenant/inventory/movements/transfer',
        input,
      )
    ).data.movements,
  reverse: async (movementId: string): Promise<InventoryMovement> =>
    (
      await api.post<{ movement: InventoryMovement }>(
        `/tenant/inventory/movements/${movementId}/reversal`,
        {},
      )
    ).data.movement,
  listReplenishment: async (): Promise<InventoryReplenishmentPolicy[]> =>
    (await api.get<{ policies: InventoryReplenishmentPolicy[] }>('/tenant/inventory/replenishment'))
      .data.policies,
  saveReplenishment: async (input: {
    productId: string;
    locationId: string;
    minimumQuantity: string;
    reorderPoint: string;
  }): Promise<InventoryReplenishmentPolicy> =>
    (
      await api.put<{ policy: InventoryReplenishmentPolicy }>(
        '/tenant/inventory/replenishment',
        input,
      )
    ).data.policy,
  removeReplenishment: async (productId: string, locationId: string): Promise<void> => {
    await api.delete('/tenant/inventory/replenishment', { params: { productId, locationId } });
  },
};

export type ProtocolActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type ProtocolSort = 'displayName' | 'serviceDisplayName' | 'createdAt';
export type ProtocolDirection = 'asc' | 'desc';

export interface ProtocolStep {
  id: string;
  position: number;
  title: string;
  description: string | null;
  estimatedMinutes: number | null;
}

export interface Protocol {
  id: string;
  serviceId: string;
  serviceDisplayName: string;
  displayName: string;
  description: string | null;
  steps: ProtocolStep[];
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProtocolStepInput {
  id?: string | undefined;
  title: string;
  description: string | null;
  estimatedMinutes: number | null;
}

export interface ProtocolInput {
  serviceId: string;
  displayName: string;
  description: string | null;
  steps: ProtocolStepInput[];
}

export interface ProtocolMaterialProduct {
  id: string;
  displayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  active: boolean;
}
export interface ProtocolMaterial {
  id: string;
  protocolStepId: string;
  productId: string;
  productDisplayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  quantity: string;
}
export interface ProtocolMaterialInput {
  protocolStepId: string;
  productId: string;
  quantity: string;
}

export interface ProtocolFilters {
  search?: string | undefined;
  activity: ProtocolActivityFilter;
  service: string;
  /// O recorte de dias sobre a data de cadastro, lido no fuso da empresa pelo servidor.
  from?: string | undefined;
  to?: string | undefined;
  sort: ProtocolSort;
  direction: ProtocolDirection;
  limit: number;
  offset: number;
}

/// O Prontuário (ADR 0038). A forma dos blocos e das respostas vem do contrato compartilhado, que
/// é o mesmo que o servidor usa para conferir e para montar o Quadro de saúde.
export interface RecordHealthTheme extends HealthTheme {
  createdByDisplayName: string | null;
  createdAt: string | null;
  usage: { templateName: string; questionLabel: string }[];
  inUse: boolean;
}

export interface RecordHealthThemeInput {
  displayName: string;
  color: HealthThemeColor;
  flagsDivergence: boolean;
}

export interface RecordTemplateSummary {
  id: string;
  displayName: string;
  kind: RecordTemplateKind;
  /// Ordem manual; nula em todos quando vale a alfabética.
  position: number | null;
  allServices: boolean;
  serviceIds: string[];
  signaturePolicy: RecordSignaturePolicy;
  publishedVersion: number;
  hasDraft: boolean;
  active: boolean;
  publishedAt: string | null;
  publishedByDisplayName: string | null;
  questionCount: number;
  updatedAt: string;
}

export interface RecordTemplateDetail extends RecordTemplateSummary {
  blocks: RecordBlock[];
  publishedBlocks: RecordBlock[];
  versions: {
    version: number;
    publishedAt: string;
    publishedByDisplayName: string;
    questionCount: number;
  }[];
}

export interface RecordTemplateSettings {
  displayName: string;
  allServices: boolean;
  serviceIds: string[];
  signaturePolicy: RecordSignaturePolicy;
  /// Só vale ao criar: o tipo do modelo não muda depois.
  kind?: RecordTemplateKind;
}

export interface RecordServiceOptions {
  available: boolean;
  services: { id: string; displayName: string; active: boolean }[];
}

/// Um documento clínico assinado (ADR 0040, decisão 7): uma ficha ou o prontuário, na tela pelas
/// duas pessoas ou no papel. O legado veio da assinatura por versão e só tem a da cliente.
export interface ClinicalDocumentSummary {
  id: string;
  kind: 'RECORD' | 'CHART';
  method: 'DIGITAL' | 'PHYSICAL';
  legacy: boolean;
  clientId: string;
  recordId: string | null;
  businessUnitId: string | null;
  unitName: string | null;
  versionIds: string[];
  contentSha256: string;
  /// O resumo recalculado agora confere com o gravado ao assinar.
  intact: boolean;
  clientStrokes: SignatureStroke[] | null;
  professionalStrokes: SignatureStroke[] | null;
  professionalDisplayName: string | null;
  device: string | null;
  file: { fileName: string; mimeType: string; sizeBytes: number } | null;
  collectedByDisplayName: string;
  signedAt: string;
}

/// De uma ficha, pela versão, ou do prontuário inteiro.
export type ClinicalDocumentScope =
  { kind: 'RECORD'; versionId: string } | { kind: 'CHART'; clientId: string };

/// Quem assina como profissional: um profissional da Agenda, quem colhe (só sem Agenda) ou ninguém.
export type ProfessionalChoice = { resourceId: string } | { self: true } | null;

export interface DocumentProfessionals {
  agenda: boolean;
  professionals: { id: string; displayName: string }[];
  suggestedId: string | null;
  /// O nome de quem está conectado, para assinar como profissional sem Agenda.
  self: string;
}

export interface ClientRecordVersion {
  id: string;
  version: number;
  templateVersionId: string;
  answers: RecordAnswers;
  recordedAt: string;
  recordedByDisplayName: string;
}

export interface ClientRecord {
  id: string;
  templateId: string;
  currentVersion: number;
  versions: ClientRecordVersion[];
}

export interface ClientAlert {
  id: string;
  text: string;
  createdByDisplayName: string;
  createdAt: string;
  closedAt: string | null;
  closeReason: string | null;
  closedByDisplayName: string | null;
}

export interface ClientChartTemplate {
  id: string;
  displayName: string;
  allServices: boolean;
  serviceIds: string[];
  signaturePolicy: RecordSignaturePolicy;
  active: boolean;
  latestVersionId: string;
  latestVersion: number;
  applicable: boolean;
}

export interface ClientChart {
  client: { id: string; displayName: string };
  themes: HealthTheme[];
  templates: ClientChartTemplate[];
  templateVersions: { id: string; templateId: string; version: number; blocks: RecordBlock[] }[];
  records: ClientRecord[];
  documents: ClinicalDocumentSummary[];
  alerts: ClientAlert[];
  health: {
    declarations: HealthDeclaration[];
    divergences: HealthDivergence[];
    occurrences: HealthOccurrence[];
    activeAlertCount: number;
    points: number;
  };
  services: { id: string; displayName: string }[] | null;
  /// Os atendimentos concluídos, com o registro da sessão; nulo sem Agenda.
  sessions: SessionVisit[] | null;
}

/// Uma intercorrência de sessão, que fica no Quadro de saúde para sempre (ADR 0038, decisão 10).
export interface SessionOccurrence {
  type: OccurrenceType;
  severity: OccurrenceSeverity;
  description: string;
  conduct: string | null;
  products: { productId: string; displayName: string }[];
}

export interface HealthOccurrence extends SessionOccurrence {
  appointmentId: string;
  serviceDisplayName: string;
  recordedAt: string;
  recordedByDisplayName: string;
}

/// O registro da sessão de um atendimento (ADR 0038, adendo de 2026-09-28).
export interface SessionRecord {
  id: string;
  clientId: string;
  appointmentId: string;
  serviceId: string;
  serviceDisplayName: string;
  check: {
    templateId: string;
    templateVersionId: string;
    answers: RecordAnswers;
    notes: string | null;
    recordedAt: string;
    recordedByDisplayName: string;
    versionIds: string[];
  } | null;
  outcome: {
    occurrence: SessionOccurrence | null;
    highlight: string | null;
    alertId: string | null;
    recordedAt: string;
    recordedByDisplayName: string;
  } | null;
  completion: { completedAt: string; versionIds: string[] } | null;
}

export interface SessionMaterialLine {
  productId: string;
  displayName: string;
  quantity: string;
  baseUnit: string;
}

/// Um atendimento concluído visto do Prontuário: protocolo, material e o registro da sessão.
export interface SessionVisit {
  appointmentId: string;
  serviceId: string;
  startsAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  serviceDisplayName: string;
  professionalDisplayName: string;
  protocol: string | null;
  materials: SessionMaterialLine[] | null;
  session: SessionRecord | null;
  checkLines: { label: string; answer: string }[];
}

/// O que a página do atendimento mostra do Prontuário.
export interface AppointmentSession {
  appointment: {
    id: string;
    clientId: string;
    serviceId: string;
    serviceDisplayName: string;
    professionalDisplayName: string;
    startsAt: string;
    /// Retrato do fuso da unidade do fato (ADR 0044).
    timeZone?: string;
    status: 'SCHEDULED' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  };
  locked: boolean;
  checkTemplates: {
    id: string;
    displayName: string;
    versionId: string;
    version: number;
    blocks: RecordBlock[];
  }[];
  session: SessionRecord | null;
  summary: {
    lastVisit: (SessionVisit & { days: number }) | null;
    lastSameService: (SessionVisit & { days: number }) | null;
  };
  products: { productId: string; displayName: string }[] | null;
}

export interface SessionOutcomeInput {
  occurrence: {
    type: OccurrenceType;
    severity: OccurrenceSeverity;
    description: string;
    conduct: string | null;
    productIds: string[];
  } | null;
  alertText: string | null;
  highlight: string | null;
}

export interface ClientHealthCount {
  clientId: string;
  records: number;
  points: number;
  divergences: number;
}

export interface RecordVersionInput {
  templateId: string;
  templateVersionId: string;
  expectedVersion: number;
  answers: RecordAnswers;
}

export const recordsApi = {
  listThemes: async (): Promise<RecordHealthTheme[]> =>
    (await api.get<{ themes: RecordHealthTheme[] }>('/tenant/records/health-themes')).data.themes,
  createTheme: async (input: RecordHealthThemeInput): Promise<void> => {
    await api.post('/tenant/records/health-themes', input);
  },
  updateTheme: async (themeId: string, input: RecordHealthThemeInput): Promise<void> => {
    await api.patch(`/tenant/records/health-themes/${themeId}`, input);
  },
  setThemeActive: async (themeId: string, active: boolean): Promise<void> => {
    await api.put(`/tenant/records/health-themes/${themeId}/activation`, { active });
  },
  deleteTheme: async (themeId: string): Promise<void> => {
    await api.delete(`/tenant/records/health-themes/${themeId}`);
  },
  listTemplates: async (): Promise<RecordTemplateSummary[]> =>
    (await api.get<{ templates: RecordTemplateSummary[] }>('/tenant/records/templates')).data
      .templates,
  getTemplate: async (templateId: string): Promise<RecordTemplateDetail> =>
    (await api.get<{ template: RecordTemplateDetail }>(`/tenant/records/templates/${templateId}`))
      .data.template,
  listServices: async (): Promise<RecordServiceOptions> =>
    (await api.get<RecordServiceOptions>('/tenant/records/services')).data,
  createTemplate: async (input: RecordTemplateSettings): Promise<RecordTemplateDetail> =>
    (await api.post<{ template: RecordTemplateDetail }>('/tenant/records/templates', input)).data
      .template,
  updateTemplate: async (
    templateId: string,
    input: RecordTemplateSettings,
  ): Promise<RecordTemplateDetail> =>
    (
      await api.patch<{ template: RecordTemplateDetail }>(
        `/tenant/records/templates/${templateId}`,
        input,
      )
    ).data.template,
  saveDraft: async (templateId: string, blocks: RecordBlock[]): Promise<RecordTemplateDetail> =>
    (
      await api.put<{ template: RecordTemplateDetail }>(
        `/tenant/records/templates/${templateId}/draft`,
        blocks,
      )
    ).data.template,
  discardDraft: async (templateId: string): Promise<RecordTemplateDetail> =>
    (
      await api.delete<{ template: RecordTemplateDetail }>(
        `/tenant/records/templates/${templateId}/draft`,
      )
    ).data.template,
  publish: async (templateId: string): Promise<RecordTemplateDetail> =>
    (
      await api.post<{ template: RecordTemplateDetail }>(
        `/tenant/records/templates/${templateId}/publish`,
      )
    ).data.template,
  /// A lista inteira na ordem nova, ou nula para voltar à alfabética.
  reorderTemplates: async (templateIds: string[] | null): Promise<RecordTemplateSummary[]> =>
    (
      await api.put<{ templates: RecordTemplateSummary[] }>('/tenant/records/templates/order', {
        templateIds,
      })
    ).data.templates,
  setTemplateActive: async (templateId: string, active: boolean): Promise<RecordTemplateDetail> =>
    (
      await api.put<{ template: RecordTemplateDetail }>(
        `/tenant/records/templates/${templateId}/activation`,
        { active },
      )
    ).data.template,
  getChart: async (clientId: string): Promise<ClientChart> =>
    (await api.get<{ chart: ClientChart }>(`/tenant/records/clients/${clientId}`)).data.chart,
  healthCounts: async (clientIds: readonly string[]): Promise<ClientHealthCount[]> =>
    (
      await api.get<{ counts: ClientHealthCount[] }>('/tenant/records/health-counts', {
        params: { clientIds: clientIds.join(',') },
      })
    ).data.counts,
  recordVersion: async (clientId: string, input: RecordVersionInput): Promise<ClientRecord> =>
    (
      await api.post<{ record: ClientRecord }>(
        `/tenant/records/clients/${clientId}/versions`,
        input,
      )
    ).data.record,
  createAlert: async (clientId: string, text: string): Promise<void> => {
    await api.post(`/tenant/records/clients/${clientId}/alerts`, { text });
  },
  closeAlert: async (alertId: string, reason: string): Promise<void> => {
    await api.post(`/tenant/records/alerts/${alertId}/close`, { reason });
  },
  getSession: async (appointmentId: string): Promise<AppointmentSession> =>
    (
      await api.get<{ session: AppointmentSession }>(
        `/tenant/records/appointments/${appointmentId}/session`,
      )
    ).data.session,
  saveSessionCheck: async (
    appointmentId: string,
    input: {
      templateId: string;
      templateVersionId: string;
      answers: RecordAnswers;
      notes: string | null;
    },
  ): Promise<SessionRecord> =>
    (
      await api.put<{ session: SessionRecord }>(
        `/tenant/records/appointments/${appointmentId}/session/check`,
        input,
      )
    ).data.session,
  saveSessionOutcome: async (
    appointmentId: string,
    input: SessionOutcomeInput,
  ): Promise<SessionRecord> =>
    (
      await api.put<{ session: SessionRecord }>(
        `/tenant/records/appointments/${appointmentId}/session/outcome`,
        input,
      )
    ).data.session,
  professionals: async (): Promise<DocumentProfessionals> =>
    (await api.get<DocumentProfessionals>('/tenant/records/professionals')).data,
  /// O papel para assinar à mão, com a linha da profissional escolhida ou em branco.
  printPaper: async (scope: ClinicalDocumentScope, professional: string | null): Promise<Blob> =>
    (
      await api.get<Blob>(
        scope.kind === 'RECORD'
          ? `/tenant/records/versions/${scope.versionId}/pdf`
          : `/tenant/records/clients/${scope.clientId}/pdf`,
        { responseType: 'blob', params: professional ? { professional } : {} },
      )
    ).data,
  signDocument: async (input: {
    scope: ClinicalDocumentScope;
    clientStrokes: SignatureStroke[];
    professional: ProfessionalChoice;
    professionalStrokes: SignatureStroke[] | null;
    pointer: 'touch' | 'pen' | 'mouse' | null;
  }): Promise<void> => {
    await api.post('/tenant/records/documents', input);
  },
  /// A via assinada sobe em base64, como o comprovante: quem decide o formato é o conteúdo.
  attachDocumentCopy: async (scope: ClinicalDocumentScope, file: File): Promise<void> => {
    await api.post('/tenant/records/documents/signed-copy', {
      scope,
      fileName: file.name,
      content: await fileToBase64(file),
    });
  },
  /// O documento assinado: o PDF do retrato ou a via física como foi anexada.
  documentFileUrl: (documentId: string): string =>
    `${api.defaults.baseURL ?? ''}/tenant/records/documents/${documentId}/file`,
  documentFile: async (documentId: string): Promise<Blob> =>
    (
      await api.get<Blob>(`/tenant/records/documents/${documentId}/file`, {
        responseType: 'blob',
      })
    ).data,
};

/// Os problemas que o servidor apontou por bloco ou por pergunta, para a tela levar cada um ao seu
/// lugar em vez de mostrar só a primeira frase.
export function getApiIssues(error: unknown): RecordIssue[] {
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const issues = (body as { details?: { issues?: unknown } } | undefined)?.details?.issues;
  return Array.isArray(issues) ? (issues as RecordIssue[]) : [];
}

export const protocolsApi = {
  list: async (filters: ProtocolFilters): Promise<{ protocols: Protocol[]; total: number }> =>
    (
      await api.get<{ protocols: Protocol[]; total: number }>('/tenant/protocols', {
        params: filters,
      })
    ).data,
  get: async (protocolId: string): Promise<Protocol> =>
    (await api.get<{ protocol: Protocol }>(`/tenant/protocols/${protocolId}`)).data.protocol,
  create: async (input: ProtocolInput): Promise<Protocol> =>
    (await api.post<{ protocol: Protocol }>('/tenant/protocols', input)).data.protocol,
  update: async (protocolId: string, input: ProtocolInput): Promise<Protocol> =>
    (await api.patch<{ protocol: Protocol }>(`/tenant/protocols/${protocolId}`, input)).data
      .protocol,
  setActive: async (protocolId: string, active: boolean): Promise<Protocol> =>
    (
      await api.put<{ protocol: Protocol }>(`/tenant/protocols/${protocolId}/activation`, {
        active,
      })
    ).data.protocol,
  listMaterialProducts: async (search?: string): Promise<ProtocolMaterialProduct[]> =>
    (
      await api.get<{ products: ProtocolMaterialProduct[] }>(
        '/tenant/protocols/material-products',
        { params: search ? { search } : {} },
      )
    ).data.products,
  getMaterials: async (protocolId: string): Promise<ProtocolMaterial[]> =>
    (await api.get<{ materials: ProtocolMaterial[] }>(`/tenant/protocols/${protocolId}/materials`))
      .data.materials,
  replaceMaterials: async (
    protocolId: string,
    input: ProtocolMaterialInput[],
  ): Promise<ProtocolMaterial[]> =>
    (
      await api.put<{ materials: ProtocolMaterial[] }>(
        `/tenant/protocols/${protocolId}/materials`,
        input,
      )
    ).data.materials,
};

export interface MaterialUsageLine {
  id: string;
  businessUnitId: string;
  appointmentId: string;
  /// A aplicação de onde veio o previsto, quando houve protocolo aplicado.
  applicationId: string | null;
  productId: string;
  productDisplayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  locationId: string;
  locationDisplayName: string;
  lotId: string | null;
  lotCode: string | null;
  quantity: string;
  plannedQuantity: string;
  note: string | null;
  occurredAt: string;
  movementId: string;
  registeredByDisplayName: string;
  reversedAt: string | null;
  reversalReason: string | null;
  reversalMovementId: string | null;
  reversedByDisplayName: string | null;
  createdAt: string;
}

export interface MaterialComparisonRow {
  productId: string;
  productDisplayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  plannedQuantity: string;
  usedQuantity: string;
  differenceQuantity: string;
}

/// `NO_PLAN` = não havia previsto para comparar (ADR 0030, decisão 8).
export type MaterialUsageState = 'NO_PLAN' | 'PENDING' | 'MATCHED' | 'DIVERGENT';

export interface ConsumedAppointment {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  clientDisplayName: string;
  serviceDisplayName: string;
  professionalDisplayName: string;
  startsAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  status: AppointmentStatus;
}

export interface MaterialUsageSummary {
  appointment: ConsumedAppointment;
  /// O protocolo do atendimento, quando houver. Nulo sem protocolo ou sem Protocolos contratado.
  application: {
    id: string;
    protocolDisplayName: string;
    status: 'PLANNED' | 'APPLIED';
  } | null;
  materials: MaterialComparisonRow[];
  state: MaterialUsageState;
  lineCount: number;
}

export interface MaterialUsageDetail extends MaterialUsageSummary {
  lines: MaterialUsageLine[];
}

export interface ConsumableProduct {
  id: string;
  displayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  requiresLot: boolean;
  active: boolean;
}

export interface ConsumptionLocation {
  id: string;
  displayName: string;
  active: boolean;
}

export interface ConsumptionLotOption {
  id: string;
  code: string;
  expiresOn: string | null;
  balance: string;
  suggested: boolean;
}

export interface MaterialUsageSettings {
  defaultLocationId: string | null;
  defaultLocationDisplayName: string | null;
}

export interface MaterialUsageFilters {
  search?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  limit: number;
  offset: number;
}

export type MaterialSufficiencyState = 'SUFFICIENT' | 'INSUFFICIENT';
export type MaterialSufficiencySort =
  'productDisplayName' | 'currentQuantity' | 'plannedQuantity' | 'projectedQuantity';

export interface MaterialSufficiencyRow {
  productId: string;
  productDisplayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  currentQuantity: string;
  plannedQuantity: string;
  projectedQuantity: string;
  state: MaterialSufficiencyState;
}

export interface MaterialSufficiencyFilters {
  search?: string | undefined;
  state: MaterialSufficiencyState | 'ALL';
  from?: string | undefined;
  to?: string | undefined;
  sort: MaterialSufficiencySort;
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
}

/// Uma linha do rascunho do fechamento: tudo pode faltar, é o que alguém está preenchendo.
export interface MaterialUsageDraftLine {
  productId: string | null;
  quantity: string;
  locationId: string | null;
  lotId: string | null;
  note: string | null;
}

export interface MaterialUsageDraft {
  appointmentId: string;
  lines: MaterialUsageDraftLine[];
  savedByDisplayName: string;
  savedAt: string;
}

export interface MaterialUsageLineInput {
  productId: string;
  locationId?: string | undefined;
  lotId?: string | null | undefined;
  quantity: string;
  note?: string | null | undefined;
}

export const materialUsageApi = {
  list: async (
    filters: MaterialUsageFilters,
  ): Promise<{ items: MaterialUsageSummary[]; total: number }> =>
    (
      await api.get<{ items: MaterialUsageSummary[]; total: number }>('/tenant/material-usage', {
        params: filters,
      })
    ).data,
  get: async (appointmentId: string): Promise<MaterialUsageDetail> =>
    (await api.get<{ usage: MaterialUsageDetail }>(`/tenant/material-usage/${appointmentId}`)).data
      .usage,
  register: async (
    appointmentId: string,
    lines: readonly MaterialUsageLineInput[],
  ): Promise<MaterialUsageDetail> =>
    (
      await api.post<{ usage: MaterialUsageDetail }>(
        `/tenant/material-usage/${appointmentId}/lines`,
        lines,
      )
    ).data.usage,
  getDraft: async (appointmentId: string): Promise<MaterialUsageDraft | null> =>
    (
      await api.get<{ draft: MaterialUsageDraft | null }>(
        `/tenant/material-usage/${appointmentId}/draft`,
      )
    ).data.draft,
  saveDraft: async (
    appointmentId: string,
    lines: readonly MaterialUsageDraftLine[],
  ): Promise<MaterialUsageDraft> =>
    (
      await api.put<{ draft: MaterialUsageDraft }>(
        `/tenant/material-usage/${appointmentId}/draft`,
        { lines },
      )
    ).data.draft,
  discardDraft: async (appointmentId: string): Promise<void> => {
    await api.delete(`/tenant/material-usage/${appointmentId}/draft`);
  },
  reverse: async (usageId: string, reason: string): Promise<MaterialUsageLine> =>
    (
      await api.post<{ line: MaterialUsageLine }>(
        `/tenant/material-usage/lines/${usageId}/reversal`,
        { reason },
      )
    ).data.line,
  listProducts: async (search?: string): Promise<ConsumableProduct[]> =>
    (
      await api.get<{ products: ConsumableProduct[] }>('/tenant/material-usage/products', {
        params: search ? { search } : {},
      })
    ).data.products,
  listLocations: async (): Promise<ConsumptionLocation[]> =>
    (await api.get<{ locations: ConsumptionLocation[] }>('/tenant/material-usage/locations')).data
      .locations,
  suggestLots: async (query: {
    productId: string;
    locationId: string;
    quantity: string;
  }): Promise<ConsumptionLotOption[]> =>
    (
      await api.get<{ lots: ConsumptionLotOption[] }>('/tenant/material-usage/lots', {
        params: query,
      })
    ).data.lots,
  getSettings: async (): Promise<MaterialUsageSettings> =>
    (await api.get<{ settings: MaterialUsageSettings }>('/tenant/material-usage/settings')).data
      .settings,
  saveSettings: async (defaultLocationId: string | null): Promise<MaterialUsageSettings> =>
    (
      await api.put<{ settings: MaterialUsageSettings }>('/tenant/material-usage/settings', {
        defaultLocationId,
      })
    ).data.settings,
};

export const materialSufficiencyApi = {
  list: async (
    filters: MaterialSufficiencyFilters,
  ): Promise<{ items: MaterialSufficiencyRow[]; total: number }> =>
    (
      await api.get<{ items: MaterialSufficiencyRow[]; total: number }>(
        '/tenant/material-sufficiency',
        { params: filters },
      )
    ).data,
};

export type ProtocolApplicationStatus = 'PLANNED' | 'APPLIED';
export type ProtocolApplicationStatusFilter = 'ALL' | ProtocolApplicationStatus;
export type ProtocolApplicationSort = 'appointmentStartsAt' | 'clientDisplayName' | 'createdAt';

export interface ProtocolApplicationMaterialSnapshot {
  id: string;
  productId: string;
  productDisplayName: string;
  baseUnit: 'UNIT' | 'MILLILITER' | 'GRAM';
  quantity: string;
}

export interface ProtocolApplicationStepSnapshot {
  id: string;
  position: number;
  title: string;
  description: string | null;
  estimatedMinutes: number | null;
  materials: ProtocolApplicationMaterialSnapshot[];
}

export interface ProtocolApplication {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  appointmentId: string;
  clientId: string;
  clientDisplayName: string;
  serviceDisplayName: string;
  appointmentStartsAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  protocolId: string;
  protocolDisplayName: string;
  plannedProtocolId: string | null;
  plannedProtocolDisplayName: string | null;
  status: ProtocolApplicationStatus;
  appliedAt: string | null;
  appliedByDisplayName: string | null;
  steps: ProtocolApplicationStepSnapshot[];
  createdAt: string;
  updatedAt: string;
}

export interface ApplicableAppointment {
  id: string;
  businessUnitId: string;
  clientId: string;
  clientDisplayName: string;
  serviceId: string;
  serviceDisplayName: string;
  startsAt: string;
  cancelled: boolean;
}

export interface ProtocolApplicationFilters {
  search?: string | undefined;
  status: ProtocolApplicationStatusFilter;
  protocol: string;
  from?: string | undefined;
  to?: string | undefined;
  sort: ProtocolApplicationSort;
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
}

export const protocolApplicationsApi = {
  list: async (
    filters: ProtocolApplicationFilters,
  ): Promise<{ applications: ProtocolApplication[]; total: number }> =>
    (
      await api.get<{ applications: ProtocolApplication[]; total: number }>(
        '/tenant/protocols/applications',
        { params: filters },
      )
    ).data,
  listAppointments: async (search?: string): Promise<ApplicableAppointment[]> =>
    (
      await api.get<{ appointments: ApplicableAppointment[] }>(
        '/tenant/protocols/applications/appointments',
        { params: search ? { search } : {} },
      )
    ).data.appointments,
  plan: async (input: {
    appointmentId: string;
    protocolId: string;
  }): Promise<ProtocolApplication> =>
    (await api.post<{ application: ProtocolApplication }>('/tenant/protocols/applications', input))
      .data.application,
  apply: async (input: {
    appointmentId: string;
    protocolId: string;
  }): Promise<ProtocolApplication> =>
    (
      await api.post<{ application: ProtocolApplication }>(
        '/tenant/protocols/applications/apply',
        input,
      )
    ).data.application,
  removePlanned: async (applicationId: string): Promise<void> => {
    await api.delete(`/tenant/protocols/applications/${applicationId}`);
  },
  /// A aplicação do atendimento, planejada ou aplicada. Nulo é atendimento sem protocolo.
  findByAppointment: async (appointmentId: string): Promise<ProtocolApplication | null> =>
    (
      await api.get<{ application: ProtocolApplication | null }>(
        `/tenant/protocols/applications/by-appointment/${appointmentId}`,
      )
    ).data.application,
};

export type FinanceAccountKind = 'BANK' | 'CASH' | 'ACQUIRER';
export type FinanceAccountActivity = 'ACTIVE' | 'INACTIVE';
export type FinanceMovementKind = 'OPENING_BALANCE' | 'MANUAL';
export type FinanceMovementDirection = 'INFLOW' | 'OUTFLOW';
export type FinanceMovementStatus = 'ACTIVE' | 'CANCELED';
export type FinanceMovementSort = 'occurredOn' | 'amount' | 'createdAt';
export type FinancialObligationDirection = 'PAYABLE' | 'RECEIVABLE';
export type FinancialObligationStatus = 'OPEN' | 'OVERDUE' | 'PAID' | 'CANCELED';
/// As situações que o filtro combina, sem sobreposição: "Em aberto" é marcar A vencer e Vencidas.
/// Na parcela, `OPEN` é a que tem saldo e ainda não venceu — o que o filtro chama de `UPCOMING`.
export type FinancialObligationStatusChoice = 'UPCOMING' | 'OVERDUE' | 'PAID' | 'CANCELED';
export interface FinancialObligationCounterparty {
  name: string;
  count: number;
}
export type FinancialObligationSort =
  'dueOn' | 'competenceOn' | 'amount' | 'description' | 'counterpartyName';

export interface FinanceAccount {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  displayName: string;
  kind: FinanceAccountKind;
  currency: Currency;
  institutionName: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceAccountInput {
  businessUnitId?: string | null;
  displayName: string;
  kind: FinanceAccountKind;
  currency: Currency;
  institutionName: string | null;
}

/// Os filtros de escolha múltipla vão no singular, como a URL os repete; vazio é "todos".
export interface FinanceAccountFilters {
  search?: string | undefined;
  activity?: FinanceAccountActivity[] | undefined;
  kind?: FinanceAccountKind[] | undefined;
  currency?: Currency[] | undefined;
  sort: 'displayName' | 'createdAt' | 'kind' | 'currency';
  direction: 'asc' | 'desc';
}

export interface FinanceMovement {
  transferGroupId?: string | null;
  id: string;
  financeAccountId: string;
  occurredOn: string;
  kind: FinanceMovementKind;
  direction: FinanceMovementDirection;
  amountCents: number;
  description: string;
  status: FinanceMovementStatus;
  canceledAt: string | null;
  canceledReason: string | null;
  createdAt: string;
  updatedAt: string;
  importId?: string | null;
  importLineNumber?: number | null;
}

export interface FinanceMovementPage {
  movements: FinanceMovement[];
  total: number;
  inflowCents: number;
  outflowCents: number;
  openingBalanceCents: number;
  closingBalanceCents: number;
  currency: Currency;
}

export interface FinanceMovementInput {
  financeAccountId: string;
  occurredOn: string;
  kind: FinanceMovementKind;
  direction: FinanceMovementDirection;
  amountCents: number;
  description: string;
}

export interface FinanceMovementFilters {
  financeAccountId: string;
  from?: string | undefined;
  to?: string | undefined;
  flow?: FinanceMovementDirection[] | undefined;
  status?: FinanceMovementStatus[] | undefined;
  search?: string | undefined;
  sort: FinanceMovementSort;
  direction: 'asc' | 'desc';
  limit: number;
  offset: number;
}

export interface FinancialObligation {
  reimbursement?: Reimbursement | null | undefined;
  familyTotals?: { amountCents: number; paidCents: number; outstandingCents: number };
  paymentInstructions?: PaymentInstructions | null;
  id: string;
  businessUnitId: string;
  target: SettlementTarget;
  financeAccountId: string | null;
  sourceKey: string;
  sourceLabel: string;
  cardBreakdown?:
    { grossCents: number; cardFeeCents: number; anticipationFeeCents: number } | undefined;
  editable: boolean;
  direction: FinancialObligationDirection;
  counterpartyName: string;
  description: string;
  competenceOn: string;
  dueOn: string;
  amountCents: number;
  outstandingCents: number;
  currency: Currency;
  status: FinancialObligationStatus;
  canceledAt: string | null;
  canceledReason: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface FinancialFormOptions {
  accountingAccountId?: string | null;
  idempotencyKey?: string;
  /// "Salvar e lançar ajuste" quando a competência cai num mês fechado (ADR 0064).
  closedPeriodAdjustment?: { reason: string };
}
export interface FinancialObligationInput extends FinancialFormOptions {
  reimbursement?: Reimbursement | null;
  paymentInstructions?: PaymentInstructions | null;
  financeAccountId: string | null;
  direction: FinancialObligationDirection;
  counterpartyName: string;
  description: string;
  competenceOn: string;
  dueOn: string;
  amountCents: number;
  currency: Currency;
}
/// Cada lista vazia ou ausente é "todas"; com itens, vale qualquer um deles.
export interface FinancialObligationFilters {
  origins?: string[];
  today: string;
  search?: string;
  /// Nomes exatos da contraparte; a busca acima também olha a descrição.
  counterparties?: string[];
  direction: FinancialObligationDirection | 'ALL';
  statuses?: FinancialObligationStatusChoice[];
  financeAccountIds?: string[];
  from?: string;
  to?: string;
  periodBy?: 'dueOn' | 'competenceOn';
  sort: FinancialObligationSort;
  order: 'asc' | 'desc';
  limit: number;
  offset: number;
}
export interface FinancialObligationPage {
  origins?: Array<{ value: string; label: string }>;
  summary?: Array<{
    currency: Currency;
    amountCents: number;
    paidCents: number;
    outstandingCents: number;
    familyAmountCents: number;
    familyPaidCents: number;
    familyOutstandingCents: number;
  }>;
  obligations: FinancialObligation[];
  total: number;
  totals: Array<{ currency: Currency; payableCents: number; receivableCents: number }>;
}

export interface SettlementTarget {
  sourceKey: string;
  entity: string;
  externalId: string;
  occurrence: string;
}
export interface SettlementLink {
  id: string;
  movementId: string;
  target: SettlementTarget;
  producerRef: string | null;
  amountCents: number;
  settledOn: string;
  createdAt: string;
}
export interface ReconciliationMovement {
  id: string;
  financeAccountId: string;
  accountName: string;
  occurredOn: string;
  direction: FinanceMovementDirection;
  description: string;
  amountCents: number;
  settledCents: number;
  currency: Currency;
  ignoredAt: string | null;
  ignoredReason: string | null;
  /// A sobra que alguém já explicou (ADR 0026). Enquanto ela existir, este pagamento está tratado
  /// por inteiro e não cabe em mais nenhum compromisso.
  difference: MovementDifference | null;
}
export type FinanceDifferenceReason = 'ROUNDING' | 'BANK_FEE' | 'CONFIRMED_DIFFERENCE';
export interface MovementDifference {
  id: string;
  amountCents: number;
  reason: FinanceDifferenceReason;
  note: string | null;
  resolvedAt: string;
}
export interface ReconciliationObligation {
  key: string;
  target: SettlementTarget;
  family: ReconciliationRuleTarget | null;
  sourceLabel: string;
  financeAccountId: string | null;
  direction: FinancialObligationDirection;
  counterpartyName: string;
  description: string;
  competenceOn: string;
  dueOn: string;
  amountCents: number;
  outstandingCents: number;
  /// Baixado à mão no módulo dono. Conciliar esta obrigação troca aquelas baixas pelo vínculo do
  /// movimento que a pagou de verdade (ADR 0025).
  manuallySettledCents: number;
  manualSettlements: Array<{ id: string; amountCents: number; settledOn: string }>;
  currency: Currency;
}
export interface ReconciliationSuggestion {
  keys: string[];
  confidence: 'HIGH' | 'MEDIUM';
  reason: string;
  ruleName: string | null;
}

export type ReconciliationRuleTarget =
  | { kind: 'RECURRENCE'; recurrenceRuleId: string }
  | { kind: 'SOURCE_FAMILY'; sourceKey: string; entity: string; externalId: string };

export type ReconciliationRuleOperator =
  'CONTAINS' | 'STARTS_WITH' | 'ENDS_WITH' | 'EQUALS' | 'ANY_OF';

export interface ReconciliationRule {
  id: string;
  name: string;
  priority: number;
  active: boolean;
  mode: 'SIMPLE' | 'REGEX';
  operator: ReconciliationRuleOperator | null;
  terms: string[];
  pattern: string;
  direction: FinanceMovementDirection | null;
  financeAccountId: string | null;
  minAmountCents: number | null;
  maxAmountCents: number | null;
  target: ReconciliationRuleTarget;
  targetLabel: string;
}

export interface ReconciliationRuleInput {
  name: string;
  active: boolean;
  mode: 'SIMPLE' | 'REGEX';
  operator?: ReconciliationRuleOperator | null;
  terms?: string[];
  pattern?: string;
  direction: FinanceMovementDirection | null;
  financeAccountId: string | null;
  minAmountCents: number | null;
  maxAmountCents: number | null;
  target: ReconciliationRuleTarget;
}

export interface ReconciliationRuleFamily {
  key: string;
  target: ReconciliationRuleTarget;
  label: string;
  hint: string;
  currency: Currency;
  openCount: number;
}

/// Uma linha do testador: o texto avaliado, quem venceu e onde o trecho foi encontrado.
/// O extrato que alimenta o testador. Quem decide qual regra vence é a própria tela, com o
/// casamento compartilhado de `@hue/contracts`: é assim que o teste responde a cada tecla.
export interface ReconciliationRuleMovementPage {
  movements: ReconciliationMovement[];
  total: number;
}

/// A regra preenchida que a API oferece depois de uma conciliação feita à mão. Ela não existe ainda:
/// só abre o formulário para revisão (ADR 0024).
export interface ReconciliationRuleProposal {
  name: string;
  term: string;
  target: ReconciliationRuleTarget;
  targetLabel: string;
  direction: FinanceMovementDirection;
}
export interface ReconciliationEntry {
  movement: ReconciliationMovement;
  links: SettlementLink[];
  suggestion: ReconciliationSuggestion | null;
}
export interface ReconciliationPage {
  entries: ReconciliationEntry[];
  total: number;
  pendingCents: number;
  candidates: ReconciliationObligation[];
}
/// Situações sem sobreposição: "A conciliar" é sem vínculo mais conciliados; vazio é todas.
export type ReconciliationState = 'UNLINKED' | 'SETTLED' | 'IGNORED';
export interface ReconciliationFilters {
  financeAccountId?: string[] | undefined;
  from?: string | undefined;
  to?: string | undefined;
  state?: ReconciliationState[] | undefined;
  flow: 'ALL' | FinanceMovementDirection;
  search?: string | undefined;
  sort?: 'occurredOn' | 'amount' | 'createdAt' | undefined;
  direction?: 'asc' | 'desc' | undefined;
  limit: number;
  offset: number;
}

/// O extrato visto de um compromisso: só o que ainda cabe pagar, na direção e na moeda dele. Busca
/// e paginação são do servidor — a primeira página não delimita o que existe.
export interface SettleableMovementFilters {
  direction: FinanceMovementDirection;
  currency: Currency;
  search?: string | undefined;
  limit: number;
  offset: number;
}
export interface SettleableMovementPage {
  movements: ReconciliationMovement[];
  total: number;
}

export const reconciliationApi = {
  list: async (filters: ReconciliationFilters): Promise<ReconciliationPage> =>
    (await api.get<ReconciliationPage>('/tenant/reconciliation', { params: filters })).data,
  settleable: async (filters: SettleableMovementFilters): Promise<SettleableMovementPage> =>
    (
      await api.get<SettleableMovementPage>('/tenant/reconciliation/settleable-movements', {
        params: filters,
      })
    ).data,
  settleMovement: async (
    movementId: string,
    targets: SettlementTarget[],
  ): Promise<{ residualCents: number; proposal: ReconciliationRuleProposal | null }> =>
    (
      await api.post<{ residualCents: number; proposal: ReconciliationRuleProposal | null }>(
        `/tenant/reconciliation/movements/${movementId}/settlements`,
        { targets },
      )
    ).data,
  settleObligation: async (
    target: SettlementTarget,
    movementIds: string[],
  ): Promise<{ residualCents: number }> =>
    (
      await api.post<{ residualCents: number }>('/tenant/reconciliation/obligations/settlements', {
        target,
        movementIds,
      })
    ).data,
  undo: async (settlementId: string): Promise<void> => {
    await api.delete(`/tenant/reconciliation/settlements/${settlementId}`);
  },
  dismiss: async (movementId: string, reason: string): Promise<void> => {
    await api.post(`/tenant/reconciliation/movements/${movementId}/dismissal`, { reason });
  },
  resume: async (movementId: string): Promise<void> => {
    await api.delete(`/tenant/reconciliation/movements/${movementId}/dismissal`);
  },
  /// O valor não vai no corpo: quem aponta a sobra é o servidor, sobre os vínculos vivos.
  resolveDifference: async (
    movementId: string,
    reason: FinanceDifferenceReason,
    note: string | null,
  ): Promise<{ amountCents: number }> =>
    (
      await api.post<{ amountCents: number }>(
        `/tenant/reconciliation/movements/${movementId}/difference`,
        { reason, note },
      )
    ).data,
  undoDifference: async (movementId: string): Promise<void> => {
    await api.delete(`/tenant/reconciliation/movements/${movementId}/difference`);
  },
};

export interface ReconciliationRuleMovementFilters {
  financeAccountId?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  includeSettled: boolean;
  limit: number;
}

export const reconciliationRulesApi = {
  list: async (): Promise<ReconciliationRule[]> =>
    (await api.get<{ rules: ReconciliationRule[] }>('/tenant/reconciliation-rules')).data.rules,
  families: async (): Promise<ReconciliationRuleFamily[]> =>
    (
      await api.get<{ families: ReconciliationRuleFamily[] }>(
        '/tenant/reconciliation-rules/families',
      )
    ).data.families,
  create: async (input: ReconciliationRuleInput): Promise<ReconciliationRule> =>
    (await api.post<{ rule: ReconciliationRule }>('/tenant/reconciliation-rules', input)).data.rule,
  update: async (ruleId: string, input: ReconciliationRuleInput): Promise<ReconciliationRule> =>
    (await api.put<{ rule: ReconciliationRule }>(`/tenant/reconciliation-rules/${ruleId}`, input))
      .data.rule,
  remove: async (ruleId: string): Promise<void> => {
    await api.delete(`/tenant/reconciliation-rules/${ruleId}`);
  },
  reorder: async (ruleIds: string[]): Promise<void> => {
    await api.post('/tenant/reconciliation-rules/order', { ruleIds });
  },
  movements: async (
    filters: ReconciliationRuleMovementFilters,
    signal?: AbortSignal,
  ): Promise<ReconciliationRuleMovementPage> =>
    (
      await api.get<ReconciliationRuleMovementPage>('/tenant/reconciliation-rules/movements', {
        params: filters,
        ...(signal ? { signal } : {}),
      })
    ).data,
};

export interface FinancialClosingAccount {
  financeAccountId: string;
  accountName: string;
  currency: Currency;
  openingBalanceCents: number;
  inflowCents: number;
  outflowCents: number;
  closingBalanceCents: number;
  movementCount: number;
  settledCount: number;
  ignoredCount: number;
  resolvedCount: number;
  pendingCount: number;
}
export interface FinancialClosingBlocker {
  movementId: string;
  financeAccountId: string;
  accountName: string;
  occurredOn: string;
  description: string;
  direction: FinanceMovementDirection;
  amountCents: number;
  settledCents: number;
  currency: Currency;
}
export interface FinancialClosingAlert {
  currency: Currency;
  direction: 'PAYABLE' | 'RECEIVABLE';
  count: number;
  outstandingCents: number;
}
interface FinancialClosingShape {
  periodMonth: string;
  reopenedAt: string | null;
  reopenedReason: string | null;
  accounts: FinancialClosingAccount[];
  blockers: FinancialClosingBlocker[];
  blockerCount: number;
  alerts: FinancialClosingAlert[];
  chain: {
    startMonth: string | null;
    previousMonth: string;
    previousClosed: boolean;
    latestClosedMonth: string | null;
  };
  canClose: boolean;
  canReopen: boolean;
}
/// O mês fechado sempre tem versão e instante: eles nascem com a fotografia. Deixá-los opcionais
/// obrigaria a tela a inventar um valor para um estado que não existe.
export type FinancialClosing =
  | (FinancialClosingShape & { status: 'OPEN'; version: number | null; closedAt: null })
  | (FinancialClosingShape & { status: 'CLOSED'; version: number; closedAt: string });

export const financialClosingApi = {
  get: async (month: string): Promise<FinancialClosing> =>
    (
      await api.get<{ closing: FinancialClosing }>('/tenant/financial-closing', {
        params: { month },
      })
    ).data.closing,
  close: async (month: string): Promise<FinancialClosing> =>
    (await api.post<{ closing: FinancialClosing }>('/tenant/financial-closing', { month })).data
      .closing,
  reopen: async (month: string, reason: string): Promise<FinancialClosing> =>
    (
      await api.post<{ closing: FinancialClosing }>('/tenant/financial-closing/reopen', {
        month,
        reason,
      })
    ).data.closing,
};

export const financeAccountsApi = {
  list: async (filters: FinanceAccountFilters): Promise<FinanceAccount[]> =>
    (
      await api.get<{ accounts: FinanceAccount[] }>('/tenant/financial-accounts', {
        params: filters,
      })
    ).data.accounts,
  create: async (input: FinanceAccountInput): Promise<FinanceAccount> =>
    (await api.post<{ account: FinanceAccount }>('/tenant/financial-accounts', input)).data.account,
  update: async (
    accountId: string,
    input: Pick<FinanceAccountInput, 'displayName' | 'institutionName'>,
  ): Promise<FinanceAccount> =>
    (await api.patch<{ account: FinanceAccount }>(`/tenant/financial-accounts/${accountId}`, input))
      .data.account,
  setActive: async (accountId: string, active: boolean): Promise<FinanceAccount> =>
    (
      await api.put<{ account: FinanceAccount }>(
        `/tenant/financial-accounts/${accountId}/activation`,
        { active },
      )
    ).data.account,
};

export interface StatementImportSummary {
  id: string;
  originalFileName: string;
  importedRows: number;
  activeRows: number;
  withdrawnRows: number;
  from: string | null;
  to: string | null;
  inflowCents: number;
  outflowCents: number;
  currency: string;
  blockers: string[];
}
export interface StatementImportHistoryPage {
  movements: Array<{
    id: string;
    description: string;
    occurredOn: string;
    amountCents: number;
    direction: string;
    status: string;
    reason: string | null;
  }>;
  events: Array<{
    id: string;
    movementId: string;
    summary: string;
    actor: string;
    createdAt: string;
    reason: string | null;
  }>;
  total: number;
}
export const statementImportsApi = {
  history: async (
    importId: string,
    section: 'movements' | 'events',
    search: string,
    offset: number,
  ): Promise<StatementImportHistoryPage> =>
    (
      await api.get<StatementImportHistoryPage>(
        `/tenant/financial-statement-imports/${importId}/history`,
        { params: { section, search, offset, limit: 20 } },
      )
    ).data,
  restore: async (importId: string, reason: string): Promise<{ changedRows: number }> =>
    (
      await api.post<{ changedRows: number }>(
        `/tenant/financial-statement-imports/${importId}/restore`,
        { reason },
      )
    ).data,
  list: async (
    financeAccountId: string,
    search: string,
    offset: number,
  ): Promise<{ imports: StatementImportSummary[]; total: number }> =>
    (
      await api.get<{ imports: StatementImportSummary[]; total: number }>(
        '/tenant/financial-statement-imports',
        { params: { financeAccountId, search, offset, limit: 10 } },
      )
    ).data,
  undo: async (importId: string, reason: string): Promise<{ changedRows: number }> =>
    (
      await api.post<{ changedRows: number }>(
        `/tenant/financial-statement-imports/${importId}/undo`,
        { reason },
      )
    ).data,
};

export const financeMovementsApi = {
  list: async (filters: FinanceMovementFilters): Promise<FinanceMovementPage> =>
    (await api.get<FinanceMovementPage>('/tenant/financial-movements', { params: filters })).data,
  create: async (input: FinanceMovementInput): Promise<FinanceMovement> =>
    (await api.post<{ movement: FinanceMovement }>('/tenant/financial-movements', input)).data
      .movement,
  update: async (movementId: string, description: string): Promise<FinanceMovement> =>
    (
      await api.patch<{ movement: FinanceMovement }>(`/tenant/financial-movements/${movementId}`, {
        description,
      })
    ).data.movement,
  cancel: async (movementId: string, reason: string): Promise<FinanceMovement> =>
    (
      await api.post<{ movement: FinanceMovement }>(
        `/tenant/financial-movements/${movementId}/cancellation`,
        { reason },
      )
    ).data.movement,
  importStatement: async (financeAccountId: string, file: File): Promise<StatementImportResult> =>
    (
      await api.post<StatementImportResult>('/tenant/financial-statement-imports', {
        financeAccountId,
        fileName: file.name,
        contentBase64: await fileBase64(file),
      })
    ).data,
  downloadTemplate: async (format: 'csv' | 'xlsx'): Promise<Blob> =>
    (await api.get(`/tenant/financial-statement-template/${format}`, { responseType: 'blob' }))
      .data as Blob,
};

/// `classifiedRows`: quantos dos movimentos as regras da Contabilidade gerencial classificaram
/// sozinhas. Zero quando o módulo não está contratado.
export interface StatementImportResult {
  skippedRows?: number;
  importId: string;
  importedRows: number;
  classifiedRows: number;
}

/// Cada escolha múltipla vai repetida na URL, no singular (`status=A&status=B`): um nome de
/// contraparte pode ter vírgula, então juntar com vírgula não serviria.
function obligationParams({
  counterparties,
  origins,
  statuses,
  financeAccountIds,
  ...filters
}: FinancialObligationFilters) {
  return {
    params: {
      ...filters,
      ...(counterparties?.length ? { counterparty: counterparties } : {}),
      ...(origins?.length ? { origin: origins } : {}),
      ...(statuses?.length ? { status: statuses } : {}),
      ...(financeAccountIds?.length ? { financeAccountId: financeAccountIds } : {}),
    },
  };
}

export const financialObligationsApi = {
  export: async (filters: FinancialObligationFilters): Promise<Blob> => {
    try {
      return (
        await api.get<Blob>('/tenant/financial-obligations/report', {
          ...obligationParams({ ...filters, offset: 0 }),
          responseType: 'blob',
        })
      ).data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
        const blob = error.response.data;
        try {
          const text = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsText(blob);
          });
          error.response.data = JSON.parse(text) as unknown;
        } catch {
          /* Mantém o erro original quando não há corpo JSON. */
        }
      }
      throw error;
    }
  },
  list: async (filters: FinancialObligationFilters): Promise<FinancialObligationPage> =>
    (
      await api.get<FinancialObligationPage>(
        '/tenant/financial-obligations',
        obligationParams(filters),
      )
    ).data,
  counterparties: async (filters: {
    direction: FinancialObligationDirection;
    search?: string;
    limit?: number;
  }): Promise<FinancialObligationCounterparty[]> =>
    (
      await api.get<{ counterparties: FinancialObligationCounterparty[] }>(
        '/tenant/financial-obligations/counterparties',
        { params: filters },
      )
    ).data.counterparties,
  create: async (
    input: FinancialObligationInput,
    duplicateReason?: string,
  ): Promise<FinancialObligation> =>
    (
      await api.post<{ obligation: FinancialObligation }>('/tenant/financial-obligations', {
        ...input,
        ...(duplicateReason ? { duplicateReason } : {}),
      })
    ).data.obligation,
  update: async (id: string, input: FinancialObligationInput): Promise<FinancialObligation> =>
    (
      await api.put<{ obligation: FinancialObligation }>(
        `/tenant/financial-obligations/${id}`,
        input,
      )
    ).data.obligation,
  cancel: async (
    id: string,
    reason: string,
    closedPeriodAdjustment?: { reason: string },
  ): Promise<FinancialObligation> =>
    (
      await api.post<{ obligation: FinancialObligation }>(
        `/tenant/financial-obligations/${id}/cancellation`,
        { reason, ...(closedPeriodAdjustment ? { closedPeriodAdjustment } : {}) },
      )
    ).data.obligation,
  attachments: async (obligationId: string): Promise<FinancialObligationAttachment[]> =>
    (
      await api.get<{ attachments: FinancialObligationAttachment[] }>(
        `/tenant/financial-obligations/${obligationId}/attachments`,
      )
    ).data.attachments,
  /// O arquivo sobe em base64 no corpo JSON; quem decide o formato é o conteúdo, no servidor.
  attach: async (obligationId: string, file: File): Promise<FinancialObligationAttachment> =>
    (
      await api.post<{ attachment: FinancialObligationAttachment }>(
        `/tenant/financial-obligations/${obligationId}/attachments`,
        await receiptBody(file),
      )
    ).data.attachment,
  removeAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`/tenant/financial-obligation-attachments/${attachmentId}`);
  },
  /// O acesso é resolvido no servidor a partir da linha do comprovante, sob a RLS da unidade.
  attachmentFileUrl: (attachmentId: string): string =>
    `${api.defaults.baseURL ?? ''}/tenant/financial-obligation-attachments/${attachmentId}/file`,
};

/// Comprovante de uma despesa ou receita própria do Financeiro (ADR 0078).
export interface FinancialObligationAttachment {
  id: string;
  obligationId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  scanStatus: ScanStatus;
  scanDetail: string | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
}

export type FinancialRecurrenceAmountPolicy = 'FIXED' | 'VARIABLE';
export type FinancialRecurrenceResolution =
  | { status: 'MATERIALIZED'; obligationId: string; canceled: boolean }
  | { status: 'WAIVED'; reason: string }
  | { status: 'PENDING' };
export interface FinancialRecurrence {
  paymentInstructions?: PaymentInstructions | null;
  id: string;
  financeAccountId: string;
  counterpartyName: string;
  description: string;
  dueDay: number;
  amountPolicy: FinancialRecurrenceAmountPolicy;
  fixedAmountCents: number | null;
  currency: Currency;
  startsOn: string;
  endsOn: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  resolution?: FinancialRecurrenceResolution;
  /// Só no cadastro e na reativação: competências lançadas na hora e as pendentes por semelhança.
  generation?: { generated: string[]; pending: string[] };
}
export interface FinancialRecurrenceInput extends FinancialFormOptions {
  paymentInstructions?: PaymentInstructions | null;
  financeAccountId: string;
  counterpartyName: string;
  description: string;
  dueDay: number;
  amountPolicy: FinancialRecurrenceAmountPolicy;
  fixedAmountCents?: number | null;
  currency: Currency;
  startsOn: string;
  endsOn?: string | null;
}
export const financialRecurrencesApi = {
  list: async (filters: {
    month?: string;
    search?: string;
    activity: 'ALL' | 'ACTIVE' | 'INACTIVE';
    policy: FinancialRecurrenceAmountPolicy | 'ALL';
    sort: 'counterpartyName' | 'dueDay' | 'createdAt';
    direction: 'asc' | 'desc';
  }): Promise<FinancialRecurrence[]> =>
    (
      await api.get<{ recurrences: FinancialRecurrence[] }>('/tenant/financial-recurrences', {
        params: filters,
      })
    ).data.recurrences,
  create: async (
    input: FinancialRecurrenceInput,
    duplicateReason?: string,
  ): Promise<FinancialRecurrence> =>
    (
      await api.post<{ recurrence: FinancialRecurrence }>('/tenant/financial-recurrences', {
        ...input,
        ...(duplicateReason ? { duplicateReason } : {}),
      })
    ).data.recurrence,
  update: async (id: string, input: FinancialRecurrenceInput): Promise<FinancialRecurrence> =>
    (
      await api.put<{ recurrence: FinancialRecurrence }>(
        `/tenant/financial-recurrences/${id}`,
        input,
      )
    ).data.recurrence,
  setActive: async (id: string, active: boolean, month: string): Promise<FinancialRecurrence> =>
    (
      await api.put<{ recurrence: FinancialRecurrence }>(
        `/tenant/financial-recurrences/${id}/activation`,
        { active, month },
      )
    ).data.recurrence,
  waive: async (
    id: string,
    month: string,
    reason: string,
  ): Promise<{ status: 'WAIVED' | 'ALREADY_WAIVED'; reason: string }> =>
    (await api.post(`/tenant/financial-recurrences/${id}/waivers`, { month, reason })).data as {
      status: 'WAIVED' | 'ALREADY_WAIVED';
      reason: string;
    },
  revokeWaiver: async (id: string, month: string): Promise<{ status: 'REVOKED' }> =>
    (await api.delete(`/tenant/financial-recurrences/${id}/waivers/${month}`)).data as {
      status: 'REVOKED';
    },
  generate: async (
    id: string,
    month: string,
    amountCents?: number,
    duplicateReason?: string,
  ): Promise<{ status: 'CREATED' | 'ALREADY_EXISTS'; obligationId: string }> =>
    (
      await api.post(`/tenant/financial-recurrences/${id}/occurrences`, {
        month,
        ...(amountCents ? { amountCents } : {}),
        ...(duplicateReason ? { duplicateReason } : {}),
      })
    ).data as { status: 'CREATED' | 'ALREADY_EXISTS'; obligationId: string },
  linkOccurrence: async (
    id: string,
    month: string,
    obligationId: string,
  ): Promise<{ status: 'LINKED'; obligationId: string }> =>
    (
      await api.post(`/tenant/financial-recurrences/${id}/occurrences/${month}/link`, {
        obligationId,
      })
    ).data as { status: 'LINKED'; obligationId: string },
};

/// O lançamento parecido que a API apontou junto do erro. Sem lista, `[]`: a tela mostra a frase e
/// segue, em vez de quebrar por causa do formato de um erro.
export interface DuplicateSuspect {
  kind: 'OBLIGATION' | 'RECURRENCE';
  id: string;
  counterpartyName: string;
  description: string;
  competenceOn: string | null;
  dueOn: string | null;
  dueDay: number | null;
  amountCents: number | null;
  currency: Currency;
  linkable: boolean;
}
export function getDuplicateSuspects(error: unknown): DuplicateSuspect[] {
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const suspects = (body as { details?: { suspects?: unknown } } | undefined)?.details?.suspects;
  return Array.isArray(suspects) ? (suspects as DuplicateSuspect[]) : [];
}

function fileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler o arquivo.'));
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result.split(',', 2)[1] ?? '');
      else reject(new Error('Não foi possível ler o arquivo.'));
    };
    reader.readAsDataURL(file);
  });
}

export type SchedulingResourceKind = 'PROFESSIONAL' | 'ROOM';
export type SchedulingActivityFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';
export type SchedulingDirection = 'asc' | 'desc';
export type SchedulingResourceSort = 'displayName' | 'kind' | 'createdAt';
export type AppointmentStatus = 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED' | 'NO_SHOW';
/// `OPEN` é o que ainda não foi fechado nem cancelado: agendado ou confirmado.
export type AppointmentStatusFilter = AppointmentStatus | 'ALL' | 'OPEN';
/// Recorte pelo relógio: `UPCOMING` começa daqui para a frente; `STARTED` já começou.
export type AppointmentTiming = 'ALL' | 'UPCOMING' | 'STARTED';
export type AppointmentSort = 'startsAt' | 'clientDisplayName' | 'serviceDisplayName';
export type ScheduleBlockSort = 'startsAt' | 'resourceDisplayName' | 'createdAt';

export interface SchedulingMember {
  membershipId: string;
  displayName: string;
}

export interface SchedulingResource {
  id: string;
  businessUnitId: string;
  kind: SchedulingResourceKind;
  displayName: string;
  /// Como o cliente vê o profissional no portal e nas mensagens; nulo usa o nome.
  nickname?: string | null | undefined;
  /// Quem este profissional é no sistema. Nulo quando ele não tem acesso — o caso comum.
  member: SchedulingMember | null;
  /// Atendimentos que cabem ao mesmo tempo: as cadeiras da sala. Profissional é sempre 1.
  capacity: number;
  active: boolean;
  /// Marcações e vínculo de trabalho usados pelas comissões (ADR 0082).
  receivesCommission?: boolean;
  attendsSchedule?: boolean;
  employmentBond?: EmploymentBond | null;
  commissionOffPayroll?: boolean;
  availability: Array<{ weekday: number; startMinute: number; endMinute: number }>;
  /// Serviços que o profissional realiza (ADR 0074); sala tem lista vazia.
  serviceIds: string[];
  /// Preço e duração próprios em alguns desses serviços; nulo usa o da unidade.
  serviceTerms?: ProfessionalServiceTerms[];
  createdAt: string;
  updatedAt: string;
}

export interface SchedulingResourceInput {
  kind: SchedulingResourceKind;
  displayName: string;
  nickname?: string | null | undefined;
  membershipId?: string | null | undefined;
  capacity?: number | undefined;
  serviceIds?: string[] | undefined;
  serviceTerms?: ProfessionalServiceTerms[] | undefined;
  commission?:
    | {
        receivesCommission: boolean;
        attendsSchedule: boolean;
        employmentBond: EmploymentBond | null;
        commissionOffPayroll: boolean;
      }
    | undefined;
  /// Expediente semanal: cada dia com as próprias faixas, até três por dia.
  availability: Array<{ weekday: number; startMinute: number; endMinute: number }>;
}

/// O cancelamento feito pelo cliente no portal, como a recepção vê (ADR 0071).
export type PortalRefundMethod = 'CASH' | 'PIX' | 'OTHER';
export interface PortalCancellationView {
  protocol: string;
  disposition: 'REFUND' | 'CREDIT';
  /// REFUND_FAILED: a devolução falhou no provedor e espera a decisão da empresa.
  status: 'PENDING' | 'DONE' | 'REQUIRES_ACTION' | 'REFUND_FAILED';
  error: string | null;
  /// Como a empresa devolveu fora da Cobrança online, depois da falha no provedor.
  refundMethod?: PortalRefundMethod | null;
  requestedAt: string;
  currency: string;
  paidCents: number;
  retainedCents: number;
  returnedCents: number;
}
export interface Appointment {
  overlapWarning?: string | null;
  /// Assistente do atendimento, usado na comissão (ADR 0082, decisão 24).
  assistantId?: string | null;
  /// Retrato da política de cancelamento aceita (ADR 0071). Nulo nos anteriores a ela.
  cancellationPolicy?: SchedulingCancellationSnapshot | null;
  /// Retrato do sinal do pedido (ADR 0058). Zero ou ausente: sem sinal.
  depositAmountCents?: number;
  depositDueAt?: string | null;
  depositExpiryAction?: 'KEEP_PENDING' | 'CANCEL';
  depositWaivedAt?: string | null;
  depositWaivedReason?: string | null;
  depositWaivedByName?: string | null;
  /// Recebimentos confirmados da venda do atendimento; nulo sem Vendas.
  depositPaidCents?: number | null;
  anyProfessional?: boolean;
  anyRoom?: boolean;
  requiresConfirmation?: boolean;
  requestedAt?: string | null;
  queuePosition?: number | null;
  prepaidBalanceId?: string | null | undefined;
  id: string;
  /// A unidade do atendimento (ADR 0039).
  businessUnitId: string;
  clientId: string;
  serviceId: string;
  professionalId: string;
  roomId: string | null;
  clientDisplayName: string;
  serviceDisplayName: string;
  professionalDisplayName: string;
  roomDisplayName: string | null;
  durationMinutes: number;
  priceCents: number;
  currency: Currency;
  startsAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  endsAt: string;
  status: AppointmentStatus;
  cancellationReason: string | null;
  cancelledAt: string | null;
  /// Quando a empresa confirmou o agendamento ao cliente (ADR 0036).
  confirmedAt: string | null;
  /// Quando e por quem o atendimento foi concluído (ADR 0030).
  completedAt: string | null;
  completedByDisplayName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppointmentConfirmation {
  appointment: Appointment;
  /// Abre o WhatsApp na conversa com o cliente, com o texto pronto. Nulo sem celular cadastrado.
  whatsappUrl: string | null;
  emailQueued: boolean;
}

/// Aviso individual da fila (ADR 0058). O WhatsApp é preparado por uma pessoa, nunca enviado pelo
/// sistema; o e-mail entra na fila de Comunicação quando o cliente tem e-mail.
export type AppointmentNoticeKind =
  'REQUESTED' | 'TIME_OCCUPIED' | 'DEPOSIT_EXPIRED' | 'PORTAL_UPDATED';
export type AppointmentNoticeState = 'PENDING' | 'PREPARED' | 'OBSOLETE';

export interface AppointmentNotice {
  internalError?: string | null;
  portalText?: string | null;
  id: string;
  appointmentId: string;
  businessUnitId: string;
  clientId: string;
  kind: AppointmentNoticeKind;
  state: AppointmentNoticeState;
  emailState: 'PENDING' | 'QUEUED' | 'SKIPPED';
  queuePosition: number | null;
  depositAmountCents: number;
  depositDueAt: string | null;
  creditCents: number;
  currency: Currency;
  clientDisplayName: string;
  serviceDisplayName: string;
  professionalDisplayName: string;
  startsAt: string;
  timeZone: string;
  createdAt: string;
  preparedAt: string | null;
  preparedByName: string | null;
}

export interface PreparedAppointmentNotice {
  notice: AppointmentNotice;
  /// Nulo sem celular cadastrado: o aviso continua pendente.
  whatsappUrl: string | null;
}

export interface WhatsAppTemplate {
  event: string;
  label: string;
  variables: string[];
  body: string;
  defaultBody: string;
  custom: boolean;
  updatedAt: string | null;
}

export interface ScheduleBlock {
  id: string;
  businessUnitId: string;
  resourceId: string;
  resourceDisplayName: string;
  startsAt: string;
  /// Retrato do fuso da unidade do fato (ADR 0044).
  timeZone?: string;
  endsAt: string;
  reason: string;
  active: boolean;
  cancelledAt: string | null;
  createdAt: string;
}

/// O calendário de um período (ADR 0034): agendamentos e bloqueios de até 42 dias, sem paginação.
export interface SchedulingCalendar {
  appointments: Appointment[];
  blocks: ScheduleBlock[];
  /// Feriados que mudam o dia em cada unidade do período (ADR 0087).
  holidays?: CalendarHoliday[] | undefined;
}

export type HolidayMode = 'CLOSED' | 'OPEN' | 'REDUCED';
/// Um feriado do ano com a situação que vale (ADR 0087). `origin`: NATIONAL é feriado nacional,
/// OPTIONAL é ponto facultativo e CUSTOM é cadastrado pela empresa.
export interface SchedulingHoliday {
  key: string;
  name: string;
  date: string;
  origin: 'NATIONAL' | 'OPTIONAL' | 'CUSTOM';
  businessUnitId: string | null;
  mode: HolidayMode;
  startMinute: number | null;
  endMinute: number | null;
  resourceIds: string[];
  decidedBy: 'DEFAULT' | 'COMPANY' | 'UNIT';
  onlyThisYear: boolean;
}
export type CalendarHoliday = SchedulingHoliday & { unitId: string };
export interface SchedulingHolidayChoiceInput {
  businessUnitId: string | null;
  holidayKey: string;
  year: number | null;
  /// Nulo apaga a escolha deste alcance e ano: o feriado volta a seguir o nível acima.
  mode: HolidayMode | null;
  startMinute: number | null;
  endMinute: number | null;
  resourceIds: string[];
}

export interface SchedulingCalendarFilters {
  /// Outra unidade que a pessoa alcança. Ausente, vale a unidade da sessão.
  businessUnitId?: string | undefined;
  from: string;
  to: string;
  /// Ausente é "todos"; presente, mesmo vazio, é "só estes".
  professionals?: string[] | undefined;
  rooms?: string[] | undefined;
  withoutRoom?: boolean | undefined;
  includeCancelled: boolean;
}

export const schedulingApi = {
  getCalendar: async (filters: SchedulingCalendarFilters): Promise<SchedulingCalendar> =>
    (
      await api.get<SchedulingCalendar>('/tenant/scheduling/calendar', {
        params: {
          ...(filters.businessUnitId ? { businessUnitId: filters.businessUnitId } : {}),
          from: filters.from,
          to: filters.to,
          includeCancelled: String(filters.includeCancelled),
          ...(filters.professionals ? { professionals: filters.professionals.join(',') } : {}),
          ...(filters.rooms
            ? { rooms: filters.rooms.join(','), withoutRoom: String(filters.withoutRoom ?? true) }
            : {}),
        },
      })
    ).data,
  listResources: async (filters: {
    businessUnitId?: string | undefined;
    search?: string | undefined;
    activity: SchedulingActivityFilter;
    kind: SchedulingResourceKind | 'ALL';
    sort: SchedulingResourceSort;
    direction: SchedulingDirection;
  }): Promise<SchedulingResource[]> =>
    (
      await api.get<{ resources: SchedulingResource[] }>('/tenant/scheduling/resources', {
        params: filters,
      })
    ).data.resources,
  members: async (): Promise<SchedulingMember[]> =>
    (await api.get<{ members: SchedulingMember[] }>('/tenant/scheduling/members')).data.members,
  createResource: async (input: SchedulingResourceInput): Promise<SchedulingResource> =>
    (await api.post<{ resource: SchedulingResource }>('/tenant/scheduling/resources', input)).data
      .resource,
  updateResource: async (
    resourceId: string,
    input: SchedulingResourceInput,
  ): Promise<SchedulingResource> =>
    (
      await api.patch<{ resource: SchedulingResource }>(
        `/tenant/scheduling/resources/${resourceId}`,
        input,
      )
    ).data.resource,
  setResourceActive: async (resourceId: string, active: boolean): Promise<SchedulingResource> =>
    (
      await api.put<{ resource: SchedulingResource }>(
        `/tenant/scheduling/resources/${resourceId}/activation`,
        { active },
      )
    ).data.resource,
  listAppointments: async (filters: {
    /// Ausentes, a lista não tem limite daquele lado: o histórico de um cliente é inteiro.
    from?: string | undefined;
    to?: string | undefined;
    search?: string | undefined;
    status: AppointmentStatusFilter;
    timing?: AppointmentTiming | undefined;
    professionalId?: string | undefined;
    roomId?: string | undefined;
    clientId?: string | undefined;
    sort: AppointmentSort;
    direction: SchedulingDirection;
    limit: number;
    offset: number;
  }): Promise<{ appointments: Appointment[]; total: number }> =>
    (
      await api.get<{ appointments: Appointment[]; total: number }>(
        '/tenant/scheduling/appointments',
        { params: filters },
      )
    ).data,
  getAppointment: async (appointmentId: string): Promise<Appointment> =>
    (
      await api.get<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}`,
      )
    ).data.appointment,
  /// O serviço já vendido que o Novo agendamento vai usar (ADR 0046).
  soldService: async (saleItemUnitId: string): Promise<SoldService> =>
    (
      await api.get<{ soldService: SoldService }>(
        `/tenant/scheduling/sold-services/${saleItemUnitId}`,
      )
    ).data.soldService,
  createAppointment: async (input: {
    assistantId?: string | null;
    prepaidBalanceId?: string | null | undefined;
    saleItemUnitId?: string | null | undefined;
    clientId: string;
    serviceId: string;
    professionalId: string;
    roomId: string | null;
    date: string;
    startTime: string;
  }): Promise<Appointment> =>
    (await api.post<{ appointment: Appointment }>('/tenant/scheduling/appointments', input)).data
      .appointment,
  rescheduleAppointment: async (
    appointmentId: string,
    input: {
      /// Só quando o atendimento muda de unidade (ADR 0039).
      businessUnitId?: string | undefined;
      professionalId: string;
      assistantId?: string | null;
      roomId: string | null;
      date: string;
      startTime: string;
      /// Só quando a duração muda (esticar). Ausente, a API mantém a duração real atual.
      durationMinutes?: number | undefined;
      /// A pessoa aceitou o preço e a duração do novo profissional (ADR 0074, decisão 13).
      applyProfessionalTerms?: boolean | undefined;
    },
  ): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/schedule`,
        input,
      )
    ).data.appointment,
  confirmAppointment: async (appointmentId: string): Promise<AppointmentConfirmation> =>
    (
      await api.put<AppointmentConfirmation>(
        `/tenant/scheduling/appointments/${appointmentId}/confirmation`,
      )
    ).data,
  noShow: async (id: string, reason: string): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(`/tenant/scheduling/appointments/${id}/no-show`, {
        reason,
      })
    ).data.appointment,
  completeAppointment: async (appointmentId: string): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/completion`,
      )
    ).data.appointment,
  reopenAppointment: async (appointmentId: string, reason: string): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/reopening`,
        { reason },
      )
    ).data.appointment,
  listNotices: async (
    filter: { state?: AppointmentNoticeState; appointmentId?: string } = {},
  ): Promise<AppointmentNotice[]> =>
    (
      await api.get<{ notices: AppointmentNotice[] }>('/tenant/scheduling/notices', {
        params: filter,
      })
    ).data.notices,
  prepareNotice: async (noticeId: string): Promise<PreparedAppointmentNotice> =>
    (await api.put<PreparedAppointmentNotice>(`/tenant/scheduling/notices/${noticeId}/preparation`))
      .data,
  dismissNotice: async (noticeId: string): Promise<AppointmentNotice> =>
    (
      await api.put<{ notice: AppointmentNotice }>(
        `/tenant/scheduling/notices/${noticeId}/dismissal`,
      )
    ).data.notice,
  cancellationPolicy: async (): Promise<SchedulingCancellationSettings> =>
    (await api.get<SchedulingCancellationSettings>('/tenant/scheduling/cancellation-policy')).data,
  /// Nulo na unidade ou no serviço volta a herdar; na empresa, volta à devolução integral.
  saveCancellationPolicy: async (
    target: { kind: 'COMPANY' } | { kind: 'UNIT' | 'SERVICE'; id: string },
    policy: SchedulingCancellationPolicy | null,
  ): Promise<SchedulingCancellationSettings> =>
    (
      await api.put<SchedulingCancellationSettings>(
        target.kind === 'COMPANY'
          ? '/tenant/scheduling/cancellation-policy/company'
          : `/tenant/scheduling/cancellation-policy/${target.kind === 'UNIT' ? 'units' : 'services'}/${target.id}`,
        { policy },
      )
    ).data,
  depositSettings: async (): Promise<SchedulingDepositSettings> =>
    (await api.get<SchedulingDepositSettings>('/tenant/scheduling/deposit-settings')).data,
  saveDepositSettings: async (
    settings: SchedulingDepositSettings,
  ): Promise<SchedulingDepositSettings> =>
    (await api.put<SchedulingDepositSettings>('/tenant/scheduling/deposit-settings', settings))
      .data,
  portalCancellation: async (appointmentId: string): Promise<PortalCancellationView | null> =>
    (
      await api.get<{ cancellation: PortalCancellationView | null }>(
        `/tenant/scheduling/appointments/${appointmentId}/portal-cancellation`,
      )
    ).data.cancellation,
  /// Devolução que falhou no provedor: `null` pede nova tentativa; o meio registra a devolução
  /// feita por fora.
  decidePortalRefund: async (
    appointmentId: string,
    method: PortalRefundMethod | null,
  ): Promise<void> => {
    await api.post(
      `/tenant/scheduling/appointments/${appointmentId}/portal-cancellation/refund-decision`,
      { method },
    );
  },
  waiveDeposit: async (appointmentId: string, reason: string): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/deposit-waiver`,
        { reason },
      )
    ).data.appointment,
  adjustDepositDue: async (
    appointmentId: string,
    input: { date: string; time: string },
  ): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/deposit-due`,
        input,
      )
    ).data.appointment,
  cancelAppointment: async (appointmentId: string, reason: string): Promise<Appointment> =>
    (
      await api.put<{ appointment: Appointment }>(
        `/tenant/scheduling/appointments/${appointmentId}/cancellation`,
        { reason },
      )
    ).data.appointment,
  listBlocks: async (filters: {
    from: string;
    to: string;
    search?: string | undefined;
    activity: SchedulingActivityFilter;
    resourceId?: string | undefined;
    sort: ScheduleBlockSort;
    direction: SchedulingDirection;
    limit: number;
    offset: number;
  }): Promise<{ blocks: ScheduleBlock[]; total: number }> =>
    (
      await api.get<{ blocks: ScheduleBlock[]; total: number }>('/tenant/scheduling/blocks', {
        params: filters,
      })
    ).data,
  createBlock: async (input: {
    resourceId: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string;
  }): Promise<ScheduleBlock> =>
    (await api.post<{ block: ScheduleBlock }>('/tenant/scheduling/blocks', input)).data.block,
  cancelBlock: async (blockId: string): Promise<ScheduleBlock> =>
    (await api.put<{ block: ScheduleBlock }>(`/tenant/scheduling/blocks/${blockId}/cancellation`))
      .data.block,
  listHolidays: async (input: {
    year: number;
    businessUnitId: string | null;
  }): Promise<{ year: number; businessUnitId: string | null; holidays: SchedulingHoliday[] }> =>
    (
      await api.get<{ year: number; businessUnitId: string | null; holidays: SchedulingHoliday[] }>(
        '/tenant/scheduling/holidays',
        {
          params: {
            year: input.year,
            ...(input.businessUnitId ? { businessUnitId: input.businessUnitId } : {}),
          },
        },
      )
    ).data,
  createHoliday: async (input: {
    businessUnitId: string | null;
    name: string;
    date: string;
    repeatsYearly: boolean;
  }): Promise<void> => {
    await api.post('/tenant/scheduling/holidays', input);
  },
  removeHoliday: async (holidayId: string): Promise<void> => {
    await api.delete(`/tenant/scheduling/holidays/${holidayId}`);
  },
  saveHolidayChoice: async (input: SchedulingHolidayChoiceInput): Promise<void> => {
    await api.put('/tenant/scheduling/holiday-choices', input);
  },
};

export interface ClientInvitationInspection extends OwnerInvitationInspection {
  client: { id: string; displayName: string };
  relationship: ClientAccessRelationship;
  /// Conta que já usa o código de segurança precisa dele; para cliente novo é opcional (ADR 0067).
  mfaRequired?: boolean;
}

export const clientInvitationApi = {
  inspectInvitation: async (token: string): Promise<ClientInvitationInspection> =>
    (await api.get<ClientInvitationInspection>(`/auth/client-invitations/${token}`)).data,
  activate: async (input: { token: string; password: string; code?: string }): Promise<void> => {
    await api.post(`/auth/client-invitations/${input.token}/activate`, {
      password: input.password,
      ...(input.code ? { code: input.code } : {}),
    });
  },
};

export type ContributionType = 'LOAN' | 'OWN_CASH' | 'COMPANY_FUNDS';
export type RepaymentMode = 'NONE' | 'FULL' | 'INSTALLMENTS';
export type ContributionStatus = 'NOT_REPAYABLE' | 'PENDING' | 'PARTIAL' | 'REPAID';
export type ContributionTypeFilter = 'ALL' | ContributionType;
export type ContributionStatusFilter = 'ALL' | ContributionStatus;
export type ContributionSort = 'contributedOn' | 'amount' | 'partner' | 'outstanding';
/// A situação da entrada do dinheiro, separada da situação da devolução.
export type DepositStatus = 'NONE' | 'PENDING' | 'PARTIAL' | 'RECEIVED';
export type ContributionDirection = 'asc' | 'desc';

export interface Repayment {
  id: string;
  partner: { id: string; displayName: string } | null;
  amountCents: number;
  paidOn: string;
  installmentNumber: number | null;
  notes: string | null;
  createdAt: string;
}

export interface ContributionShare {
  partner: { id: string; displayName: string };
  amountCents: number;
  repaidCents: number;
  outstandingCents: number;
  status: ContributionStatus;
}

export interface InterestSummary {
  totalPaidCents: number;
  interestCents: number;
  averageMonthlyInterestCents: number;
  monthlyRate: number;
  yearlyRate: number;
}

export interface ContributionDeposit {
  id: string;
  amountCents: number;
  receivedOn: string;
  origin: 'MANUAL' | 'RECONCILIATION';
  notes: string | null;
  createdAt: string;
}

export interface Contribution {
  id: string;
  businessUnitId: string;
  type: ContributionType;
  shares: ContributionShare[];
  description: string;
  amountCents: number;
  contributedOn: string;
  depositedCents: number;
  depositorName: string | null;
  deposits: ContributionDeposit[];
  receivedCents: number;
  depositOutstandingCents: number;
  depositStatus: DepositStatus;
  repaymentMode: RepaymentMode;
  expectedRepaymentOn: string | null;
  lender: string | null;
  installmentCount: number | null;
  installmentAmountCents: number | null;
  firstInstallmentOn: string | null;
  notes: string | null;
  repaidCents: number;
  outstandingCents: number;
  status: ContributionStatus;
  interest: InterestSummary | null;
  repayments: Repayment[];
  createdAt: string;
  updatedAt: string;
}

export interface ContributionTotals {
  contributedCents: number;
  repaidCents: number;
  outstandingCents: number;
  interestCents: number;
  depositOutstandingCents: number;
}

export interface ContributionPage {
  contributions: Contribution[];
  totals: ContributionTotals;
}

export interface ContributionInput {
  type: ContributionType;
  shares: Array<{ partnerId: string; amountCents: number }>;
  description: string;
  amountCents: number;
  contributedOn: string;
  depositedAmountCents: number;
  depositorName: string | null;
  repaymentMode: RepaymentMode;
  expectedRepaymentOn: string | null;
  lender: string | null;
  installmentCount: number | null;
  installmentAmountCents: number | null;
  firstInstallmentOn: string | null;
  notes: string | null;
}

export interface RepaymentInput {
  partnerId: string | null;
  amountCents: number;
  paidOn: string;
  installmentNumber: number | null;
  notes: string | null;
}

/// A entrada recebida por fora da conta cadastrada. O que passou pela conta vem da conciliação.
export interface DepositInput {
  amountCents: number;
  receivedOn: string;
  notes: string | null;
}

export interface ContributionFilters {
  search?: string | undefined;
  type: ContributionTypeFilter;
  status: ContributionStatusFilter;
  partnerId?: string | undefined;
  /// O recorte de dias sobre a data do aporte.
  from?: string | undefined;
  to?: string | undefined;
  sort: ContributionSort;
  direction: ContributionDirection;
}

export const contributionsApi = {
  list: async (filters: ContributionFilters): Promise<ContributionPage> =>
    (
      await api.get<ContributionPage>('/tenant/contributions', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          ...(filters.partnerId ? { partnerId: filters.partnerId } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          type: filters.type,
          status: filters.status,
          sort: filters.sort,
          direction: filters.direction,
        },
      })
    ).data,
  create: async (input: ContributionInput): Promise<Contribution> =>
    (await api.post<{ contribution: Contribution }>('/tenant/contributions', input)).data
      .contribution,
  update: async (contributionId: string, input: ContributionInput): Promise<Contribution> =>
    (
      await api.patch<{ contribution: Contribution }>(
        `/tenant/contributions/${contributionId}`,
        input,
      )
    ).data.contribution,
  remove: async (contributionId: string): Promise<void> => {
    await api.delete(`/tenant/contributions/${contributionId}`);
  },
  addRepayment: async (contributionId: string, input: RepaymentInput): Promise<Contribution> =>
    (
      await api.post<{ contribution: Contribution }>(
        `/tenant/contributions/${contributionId}/repayments`,
        input,
      )
    ).data.contribution,
  removeRepayment: async (contributionId: string, repaymentId: string): Promise<Contribution> =>
    (
      await api.delete<{ contribution: Contribution }>(
        `/tenant/contributions/${contributionId}/repayments/${repaymentId}`,
      )
    ).data.contribution,
  addDeposit: async (contributionId: string, input: DepositInput): Promise<Contribution> =>
    (
      await api.post<{ contribution: Contribution }>(
        `/tenant/contributions/${contributionId}/deposits`,
        input,
      )
    ).data.contribution,
  removeDeposit: async (contributionId: string, depositId: string): Promise<Contribution> =>
    (
      await api.delete<{ contribution: Contribution }>(
        `/tenant/contributions/${contributionId}/deposits/${depositId}`,
      )
    ).data.contribution,
};

export type PaymentMethod = 'PIX' | 'DEBIT' | 'CASH' | 'BANK_SLIP' | 'CREDIT';
export type PaymentTerm = 'IMMEDIATE' | 'DEFERRED' | 'INSTALLMENTS';
export type PaidBy = 'PARTNER' | 'COMPANY' | 'THIRD_PARTY';
export type EntryStatus =
  | 'TO_REIMBURSE'
  | 'PARTIALLY_REIMBURSED'
  | 'REIMBURSED'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'PAYMENT_PENDING';
export type EntrySort = 'purchasedOn' | 'dueOn' | 'amount' | 'description';
export type EntryDirection = 'asc' | 'desc';

export interface EntryInstallment {
  number: number;
  amountCents: number;
  dueOn: string | null;
  /// Quanto desta parcela já voltou. `settledOn` só vem quando a soma fecha o valor da parcela.
  settledCents: number;
  settledOn: string | null;
  notes: string | null;
}

export interface Entry {
  id: string;
  businessUnitId: string;
  purchasedOn: string;
  description: string;
  amountCents: number;
  paymentMethod: PaymentMethod;
  paymentTerm: PaymentTerm;
  paidBy: PaidBy;
  paidByPartner: { id: string; displayName: string } | null;
  thirdPartyName: string | null;
  dueOn: string | null;
  installmentCount: number | null;
  installmentAmountCents: number | null;
  installments: EntryInstallment[];
  declaredStatus: EntryStatus;
  status: EntryStatus;
  payeeName: string | null;
  reimburseTo: string | null;
  reimburseAt: string | null;
  reimbursedCents: number;
  outstandingCents: number;
  contribution: { id: string; description: string } | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface EntryTotals {
  purchasedCents: number;
  reimbursedCents: number;
  outstandingCents: number;
}

/// Só existe quando o recorte de dias vale sobre a baixa: o que voltou a sócio ou terceiro e o que
/// a empresa pagou, somando apenas as baixas dentro daqueles dias.
export interface EntryPeriodSettlements {
  reimbursedCents: number;
  paidCents: number;
}

export interface EntryPage {
  entries: Entry[];
  totals: EntryTotals;
  periodSettlements: EntryPeriodSettlements | null;
  total: number;
}

export interface EntryInput {
  purchasedOn: string;
  description: string;
  amountCents: number;
  paymentMethod: PaymentMethod;
  paymentTerm: PaymentTerm;
  paidBy: PaidBy;
  paidByPartnerId: string | null;
  thirdPartyName: string | null;
  dueOn: string | null;
  installmentCount: number | null;
  declaredStatus: EntryStatus;
  payeeName: string | null;
  /// Atalho do cadastro: a compra da empresa já paga por fora de conta cadastrada nasce com a baixa
  /// manual de todas as parcelas nesta data (ADR 0025).
  paidOutsideOn: string | null;
  reimburseTo: string | null;
  reimburseAt: string | null;
  contributionId: string | null;
  notes: string | null;
}

export type ReceiptOrigin = 'UPLOAD' | 'LINK';
export type ScanStatus = 'CLEAN' | 'NOT_SCANNED' | 'INFECTED';

export interface Receipt {
  id: string;
  entryId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  origin: ReceiptOrigin;
  sourceUrl: string | null;
  scanStatus: ScanStatus;
  scanDetail: string | null;
  uploadedBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
}

export type SlipOrigin = 'MANUAL' | 'PDF';

export interface PaymentSlip {
  id: string;
  entryId: string;
  installmentNumber: number;
  /// Os 47 dígitos, sem pontuação. A tela formata para conferência e copia estes.
  line: string;
  dueOn: string | null;
  amountCents: number;
  description: string | null;
  origin: SlipOrigin;
  createdBy: { id: string; firstName: string; lastName: string } | null;
  createdAt: string;
}

/// Um boleto lido do carnê, ainda não gravado. `alreadySaved` marca o que já está cadastrado, para
/// a tela não remarcar o que o operador já salvou.
export interface SuggestedSlip {
  installmentNumber: number;
  line: string;
  dueOn: string | null;
  amountCents: number;
  alreadySaved: boolean;
}

export interface PaymentSlipInput {
  installmentNumber: number;
  line: string;
  dueOn: string | null;
  amountCents: number | null;
  description: string | null;
  origin?: SlipOrigin | undefined;
}

export type EntryPeriodBy = 'purchasedOn' | 'dueOn' | 'settledOn';

export interface EntryFilters {
  search?: string | undefined;
  /// Os filtros de escolha aceitam várias opções. Ausente é "todas"; lista vazia é "nenhuma".
  statuses?: readonly EntryStatus[] | undefined;
  methods?: readonly PaymentMethod[] | undefined;
  /// `COMPANY`, `THIRD_PARTY` ou `partner:<id>`, misturados: é um filtro só na tela.
  payers?: readonly string[] | undefined;
  /// O recorte de dias e sobre qual data ele vale: a compra, o vencimento ou a baixa.
  from?: string | undefined;
  to?: string | undefined;
  periodBy?: EntryPeriodBy | undefined;
  sort: EntrySort;
  direction: EntryDirection;
  limit: number;
  offset: number;
}

export const entriesApi = {
  list: async (filters: EntryFilters): Promise<EntryPage> =>
    (
      await api.get<EntryPage>('/tenant/entries', {
        params: {
          ...(filters.search ? { search: filters.search } : {}),
          // Lista vazia vira parâmetro vazio, e não some: "desmarquei todas" é um recorte.
          ...(filters.statuses ? { statuses: filters.statuses.join(',') } : {}),
          ...(filters.methods ? { methods: filters.methods.join(',') } : {}),
          ...(filters.payers ? { payers: filters.payers.join(',') } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(filters.periodBy ? { periodBy: filters.periodBy } : {}),
          sort: filters.sort,
          direction: filters.direction,
          limit: filters.limit,
          offset: filters.offset,
        },
      })
    ).data,
  /// Um lançamento pelo id. A ficha dele tem endereço próprio, e quem abre o link não veio da
  /// lista: procurar na página carregada só funcionaria por coincidência.
  get: async (entryId: string): Promise<Entry> =>
    (await api.get<{ entry: Entry }>(`/tenant/entries/${entryId}`)).data.entry,
  create: async (input: EntryInput): Promise<Entry> =>
    (await api.post<{ entry: Entry }>('/tenant/entries', input)).data.entry,
  update: async (entryId: string, input: EntryInput): Promise<Entry> =>
    (await api.patch<{ entry: Entry }>(`/tenant/entries/${entryId}`, input)).data.entry,
  remove: async (entryId: string): Promise<void> => {
    await api.delete(`/tenant/entries/${entryId}`);
  },
  settle: async (
    entryId: string,
    input: { installmentNumber: number; settledOn: string; notes: string | null },
  ): Promise<Entry> =>
    (await api.post<{ entry: Entry }>(`/tenant/entries/${entryId}/settlements`, input)).data.entry,
  clear: async (entryId: string, installmentNumber: number): Promise<Entry> =>
    (
      await api.delete<{ entry: Entry }>(
        `/tenant/entries/${entryId}/settlements/${installmentNumber}`,
      )
    ).data.entry,
  receipts: async (entryId: string): Promise<Receipt[]> =>
    (await api.get<{ receipts: Receipt[] }>(`/tenant/entries/${entryId}/receipts`)).data.receipts,
  /// O arquivo sobe em base64 no corpo JSON. O tipo declarado pelo navegador não vai junto: quem
  /// decide o formato é o conteúdo, conferido no servidor.
  attachReceipt: async (entryId: string, file: File): Promise<Receipt> => {
    const content = await fileToBase64(file);
    return (
      await api.post<{ receipt: Receipt }>(`/tenant/entries/${entryId}/receipts`, {
        fileName: file.name,
        content,
      })
    ).data.receipt;
  },
  attachReceiptFromLink: async (entryId: string, url: string): Promise<Receipt> =>
    (await api.post<{ receipt: Receipt }>(`/tenant/entries/${entryId}/receipts/link`, { url })).data
      .receipt,
  removeReceipt: async (receiptId: string): Promise<void> => {
    await api.delete(`/tenant/receipts/${receiptId}`);
  },
  /// A URL do arquivo. O acesso é resolvido no servidor a partir da linha do comprovante, então
  /// esta URL não revela onde o objeto mora.
  receiptFileUrl: (receiptId: string): string =>
    `${api.defaults.baseURL ?? ''}/tenant/receipts/${receiptId}/file`,
  slips: async (entryId: string): Promise<PaymentSlip[]> =>
    (await api.get<{ slips: PaymentSlip[] }>(`/tenant/entries/${entryId}/slips`)).data.slips,
  /// Lê o carnê e devolve o que encontrou, SEM gravar: o operador confere na tela antes de salvar.
  readCarne: async (entryId: string, file: File): Promise<SuggestedSlip[]> =>
    (
      await api.post<{ slips: SuggestedSlip[] }>(`/tenant/entries/${entryId}/slips/read`, {
        fileName: file.name,
        content: await fileToBase64(file),
      })
    ).data.slips,
  /// O lote é tudo-ou-nada no servidor: ou o carnê inteiro entra, ou nenhuma parcela entra.
  saveSlips: async (entryId: string, slips: PaymentSlipInput[]): Promise<PaymentSlip[]> =>
    (await api.post<{ slips: PaymentSlip[] }>(`/tenant/entries/${entryId}/slips`, { slips })).data
      .slips,
  updateSlip: async (slipId: string, input: PaymentSlipInput): Promise<PaymentSlip> =>
    (await api.patch<{ slip: PaymentSlip }>(`/tenant/slips/${slipId}`, input)).data.slip,
  removeSlip: async (slipId: string): Promise<void> => {
    await api.delete(`/tenant/slips/${slipId}`);
  },
};

export type MetricsBase = 'ENTRIES' | 'CONTRIBUTIONS' | 'MANUAL';
export type MetricFormat = 'money' | 'percent' | 'ratio' | 'months';

/// Um indicador pronto. `value` nulo com `missing` cheio é a tela dizendo qual premissa falta —
/// nunca zero no lugar de "não sei".
export interface Metric {
  value: number | null;
  format: MetricFormat;
  missing: string[];
}

export interface Assumptions {
  metricsBase: MetricsBase | null;
  manualBaseCents: number | null;
  discountRateAnnualBp: number;
  horizonMonths: number;
  monthlyRevenueCents: number | null;
  monthlyExpenseCents: number | null;
  annualNetProfitCents: number | null;
  equityCents: number | null;
  totalAssetsCents: number | null;
  investedCapitalCents: number | null;
  waccBp: number | null;
  annualEbitdaCents: number | null;
  ebitdaMultipleHundredths: number | null;
  marketingSpendCents: number | null;
  newCustomers: number | null;
  averageTicketCents: number | null;
  contributionMarginBp: number | null;
  customerLifetimeMonths: number | null;
}

export interface InvestmentMetrics {
  base: { kind: MetricsBase | null; cents: number | null };
  totals: { entriesCents: number; contributionsCents: number };
  flow: {
    monthlyCents: number | null;
    horizonMonths: number;
    seriesCents: number[];
    discountedCumulativeCents: number[];
  };
  indicators: Record<string, Metric>;
}

export interface MetricsView {
  assumptions: Assumptions;
  metrics: InvestmentMetrics;
}

export const investmentMetricsApi = {
  get: async (): Promise<MetricsView> =>
    (await api.get<MetricsView>('/tenant/investment/metrics')).data,
  saveAssumptions: async (input: Assumptions): Promise<MetricsView> =>
    (await api.put<MetricsView>('/tenant/investment/assumptions', input)).data,
};

/// Quantas parcelas e quanto dinheiro caem numa janela de vencimento.
export interface DueBucket {
  count: number;
  cents: number;
}

/// O resumo que a tela de início mostra, somado no servidor.
export interface FinancialSummary {
  entries: EntryTotals;
  contributions: ContributionTotals;
  overdue: DueBucket;
  dueSoon: DueBucket;
  horizonDays: number;
  nextDueOn: string | null;
}

export const financialSummaryApi = {
  get: async (): Promise<FinancialSummary> =>
    (await api.get<{ summary: FinancialSummary }>('/tenant/investment/summary')).data.summary,
};

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  // Em pedaços: `String.fromCharCode(...bytes)` estoura a pilha em arquivo de alguns megabytes.
  let binary = '';
  const CHUNK = 32 * 1024;
  for (let offset = 0; offset < bytes.length; offset += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

export type AuditSort = 'createdAt' | 'actor' | 'action';
export type AuditDirection = 'asc' | 'desc';
export type AuditOriginFilter = 'ALL' | 'USER' | 'ROOT' | 'SYSTEM' | 'WEBHOOK' | 'MIGRATION';
export type AuditContextKind =
  'PLATFORM' | 'TENANT_MEMBER' | 'TENANT_CUSTOMER' | 'PLATFORM_ACTING_ON_TENANT' | 'CUSTOMER';

/// Um campo que mudou na edição, com o valor de antes e o de depois.
export interface AuditChange {
  field: string;
  label: string;
  from: unknown;
  to: unknown;
}

export interface AuditEntry {
  id: string;
  occurredAt: string;
  action: string;
  label: string;
  summary: string;
  entityType: string;
  entityLabel: string;
  entityId: string | null;
  changes: AuditChange[];
  /// O que o evento gravou, cru — o original ao lado da leitura traduzida.
  details: string | null;
  origin: Exclude<AuditOriginFilter, 'ALL'>;
  contextKind: AuditContextKind | null;
  requestId: string | null;
  ipAddress: string | null;
  actorId: string | null;
  actorName: string;
  actorEmail: string | null;
  /// Dois registros da mesma operação mostrados como um evento (cobrança online encerrada e o
  /// pagamento que ela cancelou na venda). Os registros originais continuam separados na trilha.
  group?: { title: string; explanation: string; related: AuditEntry[] } | null;
}

export interface AuditFilters {
  search?: string | undefined;
  action?: string | undefined;
  actorUserId?: string | undefined;
  /// Quem fez, para quem agiu no sistema anterior e não tem cadastro aqui: pelo e-mail, ou pelo
  /// nome quando o sistema anterior não guardou o e-mail.
  actorEmail?: string | undefined;
  actorName?: string | undefined;
  /// Telas marcadas. `undefined` = todas as que a pessoa pode ver; lista vazia = nenhuma.
  entityTypes?: readonly string[] | undefined;
  /// Dia civil, lido no fuso da empresa, inclusive nas duas pontas.
  from?: string | undefined;
  to?: string | undefined;
  origin: AuditOriginFilter;
  sort: AuditSort;
  direction: AuditDirection;
  limit: number;
  offset: number;
}

export interface AuditPage {
  entries: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
  actions: Array<{ action: string; label: string }>;
  /// `id` nulo é quem não tem cadastro aqui: a trilha migrada guarda só o nome dessa pessoa.
  actors: Array<{ id: string | null; name: string; email: string | null }>;
  entities: Array<{ entityType: string; label: string }>;
}

function auditParams(filters: AuditFilters) {
  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.action ? { action: filters.action } : {}),
    ...(filters.actorUserId ? { actorUserId: filters.actorUserId } : {}),
    ...(filters.actorEmail ? { actorEmail: filters.actorEmail } : {}),
    ...(filters.actorName ? { actorName: filters.actorName } : {}),
    // Lista vazia tem de chegar como `entityTypes=` (presente e vazio): omitir o parâmetro diria
    // "todas as telas", que é o contrário de desmarcar todas.
    ...(filters.entityTypes ? { entityTypes: filters.entityTypes.join(',') } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    origin: filters.origin,
    sort: filters.sort,
    direction: filters.direction,
    limit: filters.limit,
    offset: filters.offset,
  };
}

export const auditApi = {
  events: async (filters: AuditFilters): Promise<AuditPage> =>
    (await api.get<AuditPage>('/tenant/audit-events', { params: auditParams(filters) })).data,
  exportCsv: async (filters: AuditFilters): Promise<Blob> =>
    (
      await api.get<Blob>('/tenant/audit-events/export', {
        params: auditParams(filters),
        responseType: 'blob',
      })
    ).data,
};

export type EmailMessageStatus = 'PENDING' | 'SENT' | 'FAILED';
export type EmailMessageSort = 'createdAt' | 'recipient' | 'status';

export interface EmailMessage {
  id: string;
  tenantId: string | null;
  event: string;
  eventLabel: string;
  templateVersion: number;
  /// De qual prateleira o texto veio: o modelo da empresa ou o padrão da plataforma.
  templateOrigin: 'PLATFORM' | 'TENANT';
  recipientEmail: string;
  recipientName: string;
  subject: string | null;
  status: EmailMessageStatus;
  attempts: number;
  /// Quantas tentativas ainda restam antes de o sistema desistir.
  remainingAttempts: number;
  /// Só vem preenchido enquanto a mensagem ainda vai ser tentada de novo.
  nextAttemptAt: string | null;
  sentAt: string | null;
  lastError: string | null;
  provider: string | null;
  createdAt: string;
}

export interface EmailMessageFilters {
  search?: string | undefined;
  event?: string[] | undefined;
  status?: EmailMessageStatus[] | undefined;
  /// Dia civil, lido no fuso de quem está olhando, inclusive nas duas pontas.
  from?: string | undefined;
  to?: string | undefined;
  sort: EmailMessageSort;
  direction: AuditDirection;
  limit: number;
  offset: number;
}

export interface EmailMessagePage {
  messages: EmailMessage[];
  total: number;
  limit: number;
  offset: number;
  events: Array<{ value: string; label: string }>;
}

function outboxParams(filters: EmailMessageFilters) {
  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.event?.length ? { event: filters.event } : {}),
    ...(filters.status?.length ? { status: filters.status } : {}),
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    sort: filters.sort,
    direction: filters.direction,
    limit: filters.limit,
    offset: filters.offset,
  };
}

export const outboxApi = {
  /// A caixa de saída da empresa. O console root lê a mesma coisa por `/root/email-messages`.
  messages: async (filters: EmailMessageFilters): Promise<EmailMessagePage> =>
    (await api.get<EmailMessagePage>('/tenant/email-messages', { params: outboxParams(filters) }))
      .data,
  platformMessages: async (filters: EmailMessageFilters): Promise<EmailMessagePage> =>
    (await api.get<EmailMessagePage>('/root/email-messages', { params: outboxParams(filters) }))
      .data,
};

export type TemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type TemplateField = 'subject' | 'bodyHtml' | 'bodyText';

/// Um problema no rascunho, com onde ele está. `line` é 1-based e serve para o editor pintar a
/// linha; sem ela, "atributo não permitido" num corpo de quarenta linhas é uma caça ao tesouro.
export interface TemplateIssue {
  field: TemplateField;
  kind: string;
  message: string;
  line: number;
}

export interface TemplateDraft {
  subject: string;
  bodyHtml: string;
  bodyText: string;
}

export interface TemplateVersion {
  version: number;
  status: TemplateStatus;
  notes: string | null;
  publishedAt: string | null;
  updatedAt: string;
}

export type TemplateOrigin = 'PLATFORM' | 'TENANT';

export interface TemplateEvent {
  event: string;
  label: string;
  audience: 'PLATFORM' | 'TENANT';
  variables: string[];
  publishedVersion: number | null;
  hasDraft: boolean;
  updatedAt: string | null;
  /// De onde sai o texto hoje. Numa empresa que ainda não escreveu o dela, é `PLATFORM` — e dizer
  /// isso é o que impede alguém de olhar a tela vazia e achar que o convite sai sem texto.
  sendingOrigin: TemplateOrigin;
}

export interface TemplateDetail extends TemplateEvent {
  editing: TemplateDraft & { version: number | null; notes: string | null };
  versions: TemplateVersion[];
}

export interface TemplatePreview {
  subject: string;
  html: string;
  text: string;
  warnings: TemplateIssue[];
}

export interface TemplatesApi {
  list: () => Promise<{ templates: TemplateEvent[] }>;
  detail: (event: string) => Promise<TemplateDetail>;
  preview: (event: string, draft: TemplateDraft) => Promise<TemplatePreview>;
  saveDraft: (
    event: string,
    draft: TemplateDraft & { notes: string | null },
  ) => Promise<TemplateDetail>;
  discardDraft: (event: string) => Promise<TemplateDetail>;
  publish: (event: string, version: number) => Promise<TemplateDetail>;
  restore: (event: string, version: number) => Promise<TemplateDetail>;
  sendTest: (event: string, version: number) => Promise<{ to: string }>;
}

/// A mesma tela fala com dois caminhos: o do console root, que mexe no padrão da plataforma, e o da
/// empresa, que mexe no texto dela. Só o prefixo muda — o contrato é o mesmo, e é por isso que ele
/// é escrito uma vez só.
function templatesApiAt(base: string): TemplatesApi {
  return {
    list: async () => (await api.get<{ templates: TemplateEvent[] }>(base)).data,
    detail: async (event) => (await api.get<TemplateDetail>(`${base}/${event}`)).data,
    /// A prévia é do servidor de propósito: é a mesma função que escreve o e-mail de verdade. Uma
    /// prévia montada no navegador seria outra implementação, livre para discordar da que envia.
    preview: async (event, draft) =>
      (await api.post<TemplatePreview>(`${base}/${event}/preview`, draft)).data,
    saveDraft: async (event, draft) =>
      (await api.put<TemplateDetail>(`${base}/${event}/draft`, draft)).data,
    discardDraft: async (event) =>
      (await api.delete<TemplateDetail>(`${base}/${event}/draft`)).data,
    publish: async (event, version) =>
      (await api.post<TemplateDetail>(`${base}/${event}/publish`, { version })).data,
    restore: async (event, version) =>
      (await api.post<TemplateDetail>(`${base}/${event}/restore`, { version })).data,
    sendTest: async (event, version) =>
      (await api.post<{ to: string }>(`${base}/${event}/test`, { version })).data,
  };
}

export const emailTemplatesApi = templatesApiAt('/root/email-templates');

/// As mensagens de WhatsApp da empresa (ADR 0036): um texto por evento, sem versão.
export const whatsappTemplatesApi = {
  list: async (): Promise<WhatsAppTemplate[]> =>
    (await api.get<{ templates: WhatsAppTemplate[] }>('/tenant/whatsapp-templates')).data.templates,
  save: async (event: string, body: string): Promise<WhatsAppTemplate> =>
    (
      await api.put<{ template: WhatsAppTemplate }>(
        `/tenant/whatsapp-templates/${encodeURIComponent(event)}`,
        { body },
      )
    ).data.template,
  reset: async (event: string): Promise<WhatsAppTemplate> =>
    (
      await api.delete<{ template: WhatsAppTemplate }>(
        `/tenant/whatsapp-templates/${encodeURIComponent(event)}`,
      )
    ).data.template,
};
export const companyEmailTemplatesApi = templatesApiAt('/tenant/email-templates');

export interface CommunicationSettings {
  senderName: string;
  senderAddress: string;
  replyTo: string | null;
  passwordResetTtlMinutes: number;
  ownerInvitationTtlHours: number;
  memberInvitationTtlHours: number;
  brandName: string;
  brandAccent: string;
}

export interface CommunicationSettingsView extends CommunicationSettings {
  /// Os limites vêm do servidor para a tela dizer o que vale antes de alguém tentar salvar, em vez
  /// de descobrir no erro.
  bounds: Record<
    'passwordResetTtlMinutes' | 'ownerInvitationTtlHours' | 'memberInvitationTtlHours',
    { min: number; max: number }
  >;
}

export const communicationSettingsApi = {
  read: async (): Promise<CommunicationSettingsView> =>
    (await api.get<CommunicationSettingsView>('/root/communication-settings')).data,
  save: async (settings: CommunicationSettings): Promise<CommunicationSettingsView> =>
    (await api.put<CommunicationSettingsView>('/root/communication-settings', settings)).data,
};

/// Os problemas que a API mandou junto do erro, quando ela mandou. Fora isso, `[]` — a tela mostra
/// a frase e segue, em vez de quebrar por causa do formato de um erro.
export function getTemplateIssues(error: unknown): TemplateIssue[] {
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const issues = (body as { details?: { issues?: unknown } } | undefined)?.details?.issues;
  return Array.isArray(issues) ? (issues as TemplateIssue[]) : [];
}

export const passwordApi = {
  requestReset: async (email: string): Promise<{ accepted: true; developmentToken?: string }> =>
    (
      await api.post<{ accepted: true; developmentToken?: string }>(
        '/auth/password-reset/request',
        {
          email,
        },
      )
    ).data,
  completeReset: async (input: { token: string; password: string }): Promise<void> => {
    await api.post('/auth/password-reset/complete', input);
  },
};

export type UserMfaResult =
  | { status: 'AUTHENTICATED' }
  | {
      status: 'CONTEXT_SELECTION_REQUIRED';
      selectionToken: string;
      contexts: Array<
        | { kind: 'CUSTOMER' }
        | { kind: 'PLATFORM'; label: string }
        | {
            kind: 'TENANT_MEMBER';
            membershipId: string;
            tenantId: string;
            displayName: string;
            key: string;
            roleKeys: string[];
          }
      >;
    };

/// O que o login descobre depois de conferir a senha. `FIRST_ACCESS_REQUIRED` é quem foi criado com
/// senha inicial: falta parear o autenticador, trocar a senha, ou os dois (ADR 0032).
export type UserLoginResult =
  | Extract<UserMfaResult, { status: 'CONTEXT_SELECTION_REQUIRED' }>
  /// Quem só é cliente e não ligou o código entra só com a senha (ADR 0067).
  | { status: 'AUTHENTICATED' }
  | { status: 'MFA_REQUIRED'; challengeToken: string }
  | {
      status: 'FIRST_ACCESS_REQUIRED';
      challengeToken: string;
      mfaUri: string | null;
      mfaRequired?: boolean;
      requiresPasswordChange: boolean;
    };

export const userApi = {
  login: async (input: { email: string; password: string }): Promise<UserLoginResult> =>
    (await api.post<UserLoginResult>('/auth/login', input)).data,
  completeMfa: async (input: {
    challengeToken: string;
    code: string;
    newPassword?: string | undefined;
  }): Promise<UserMfaResult> => (await api.post<UserMfaResult>('/auth/mfa', input)).data,
  selectTenant: async (input: { selectionToken: string; tenantId: string }): Promise<void> => {
    await api.post('/auth/tenant-selection', input);
  },
  selectContext: async (
    input:
      | { selectionToken: string; kind: 'CUSTOMER' }
      | { selectionToken: string; kind: 'PLATFORM' }
      | { selectionToken: string; kind: 'TENANT_MEMBER'; tenantId: string },
  ): Promise<void> => {
    await api.post('/auth/context-selection', input);
  },
};

export type AvailableContext = Extract<
  UserMfaResult,
  { status: 'CONTEXT_SELECTION_REQUIRED' }
>['contexts'][number];

export const contextApi = {
  list: async (): Promise<AvailableContext[]> =>
    (await api.get<{ contexts: AvailableContext[] }>('/auth/contexts')).data.contexts,
  switch: async (
    target:
      { kind: 'CUSTOMER' } | { kind: 'PLATFORM' } | { kind: 'TENANT_MEMBER'; tenantId: string },
  ): Promise<void> => {
    await api.put('/auth/context', target);
  },
};

export const businessUnitApi = {
  switch: async (businessUnitId: string | null): Promise<void> => {
    await api.put('/auth/business-unit', { businessUnitId });
  },
  management: async (): Promise<BusinessUnitManagement> =>
    (await api.get<{ management: BusinessUnitManagement }>('/tenant/business-units')).data
      .management,
  create: async (input: {
    displayName: string;
    key: string;
    kind: BusinessUnitKind;
  }): Promise<BusinessUnitManagement> =>
    (await api.post<{ management: BusinessUnitManagement }>('/tenant/business-units', input)).data
      .management,
  setActive: async (businessUnitId: string, active: boolean): Promise<BusinessUnitManagement> =>
    (
      await api.patch<{ management: BusinessUnitManagement }>(
        `/tenant/business-units/${businessUnitId}`,
        { active },
      )
    ).data.management,
  update: async (
    businessUnitId: string,
    input: BusinessUnitInput,
  ): Promise<BusinessUnitManagement> =>
    (
      await api.put<{ management: BusinessUnitManagement }>(
        `/tenant/business-units/${businessUnitId}`,
        input,
      )
    ).data.management,
  findPostalCode: async (postalCode: string): Promise<PostalCodeAddress> =>
    (
      await api.get<{ address: PostalCodeAddress }>(
        `/tenant/business-units/postal-codes/${postalCode}`,
      )
    ).data.address,
  uploadLogo: async (businessUnitId: string, kind: BrandLogoKind, file: File): Promise<BrandLogo> =>
    (
      await api.put<{ logo: BrandLogo }>(
        `/tenant/business-units/${businessUnitId}/logos/${logoPath[kind]}`,
        { fileName: file.name, content: await fileToBase64(file) },
      )
    ).data.logo,
  removeLogo: async (businessUnitId: string, kind: BrandLogoKind): Promise<void> => {
    await api.delete(`/tenant/business-units/${businessUnitId}/logos/${logoPath[kind]}`);
  },
};

/// Os três formatos de logo (ADR 0040): horizontal, quadrado e o próprio para fundo escuro.
export type BrandLogoKind = 'MAIN' | 'SYMBOL' | 'DARK';
export type BrandPalette = UserAppearance['palette'];

export interface BrandLogo {
  id: string;
  kind: BrandLogoKind;
  sourceFormat: 'svg' | 'png' | 'webp';
  fileName: string;
  width: number | null;
  height: number | null;
  uploadedAt: string;
}

export type BrandLogoSet = Record<BrandLogoKind, BrandLogo | null>;

export interface CompanyContactDetails {
  contactNumbers: ContactNumber[];
  taxId: string | null;
  email: string | null;
  address: BusinessUnitAddress | null;
}
export interface CompanyProfile {
  contactDetails?: CompanyContactDetails;
  socialLinks?: SocialLink[];
  displayName: string;
  legalName: string | null;
  palette: BrandPalette;
  logos: BrandLogoSet;
  canManage: boolean;
}

/// A identidade que a casca desenha: a da empresa e a resolvida para a unidade em que a pessoa
/// está, com as logos próprias de cada unidade.
export interface Brand {
  displayName: string;
  palette: BrandPalette;
  company: BrandLogoSet;
  active: BrandLogoSet;
  units: Record<string, BrandLogoSet>;
}

const logoPath: Record<BrandLogoKind, string> = { MAIN: 'main', SYMBOL: 'symbol', DARK: 'dark' };

export const companyIdentityApi = {
  findPostalCode: async (postalCode: string): Promise<PostalCodeAddress> =>
    (
      await api.get<{ address: PostalCodeAddress }>(
        `/tenant/company-profile/postal-codes/${postalCode}`,
      )
    ).data.address,
  profile: async (): Promise<CompanyProfile> =>
    (await api.get<{ profile: CompanyProfile }>('/tenant/company-profile')).data.profile,
  update: async (input: {
    displayName: string;
    legalName: string | null;
    palette: BrandPalette;
    socialLinks?: SocialLink[];
    contactDetails?: CompanyContactDetails;
  }): Promise<CompanyProfile> =>
    (await api.put<{ profile: CompanyProfile }>('/tenant/company-profile', input)).data.profile,
  brand: async (): Promise<Brand> => (await api.get<{ brand: Brand }>('/tenant/brand')).data.brand,
  uploadLogo: async (kind: BrandLogoKind, file: File): Promise<BrandLogo> =>
    (
      await api.put<{ logo: BrandLogo }>(`/tenant/brand/logos/${logoPath[kind]}`, {
        fileName: file.name,
        content: await fileToBase64(file),
      })
    ).data.logo,
  removeLogo: async (kind: BrandLogoKind): Promise<void> => {
    await api.delete(`/tenant/brand/logos/${logoPath[kind]}`);
  },
  /// O PNG gerado pelo servidor. Vem pelo cliente da API, e não por `<img src>`, para a sessão
  /// renovada valer também aqui.
  logoFile: async (logoId: string, size: 'small' | 'large'): Promise<Blob> =>
    (
      await api.get<Blob>(`/tenant/brand/files/${logoId}`, {
        params: { size },
        responseType: 'blob',
      })
    ).data,
};

export const preferencesApi = {
  updateUserTimeZone: async (timeZone: string): Promise<{ timeZone: string }> =>
    (await api.patch<{ timeZone: string }>('/auth/preferences/time-zone', { timeZone })).data,
  updateUserAppearance: async (
    appearance: UserAppearance,
  ): Promise<{ appearance: UserAppearance }> =>
    (
      await api.put<{ appearance: UserAppearance }>('/auth/preferences/appearance', {
        appearance,
      })
    ).data,
  updateTenantTimeZone: async (timeZone: string): Promise<{ timeZone: string }> =>
    (await api.patch<{ timeZone: string }>('/tenant/preferences/time-zone', { timeZone })).data,
};

export interface ScreenShortcuts {
  favorites: string[];
  frequent: string[];
}

export const shortcutsApi = {
  read: async (): Promise<ScreenShortcuts> =>
    (await api.get<ScreenShortcuts>('/tenant/screen-shortcuts')).data,
  /// Contar a visita não é pedido de ninguém: quem abriu a tela não mandou salvar coisa nenhuma.
  /// Por isso o erro morre aqui — falhar em contar não pode virar aviso vermelho por cima de uma
  /// página que carregou bem.
  recordVisit: async (screen: string): Promise<void> => {
    try {
      await api.post('/tenant/screen-visits', { screen });
    } catch {
      /* contar visita é acessório: a tela abriu, e é isso que importa. */
    }
  },
  setFavorites: async (screens: readonly string[]): Promise<ScreenShortcuts> =>
    (await api.put<ScreenShortcuts>('/tenant/favorite-screens', { screens })).data,
};

export function getApiError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { title?: string } | undefined;
    return data?.title ?? 'Não foi possível concluir a operação.';
  }
  return 'Não foi possível conectar ao HUE•System.';
}

/// O código estável da recusa, para a tela decidir em qual campo mostrá-la.
export function getApiErrorCode(error: unknown): string | undefined {
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const code = (body as { code?: unknown } | undefined)?.code;
  return typeof code === 'string' ? code : undefined;
}

/// Campo que o servidor apontou ao recusar — o nome dele no corpo enviado. Sem isso, a recusa é
/// uma frase solta junto ao botão.
export function getApiErrorField(error: unknown): string | undefined {
  const body: unknown = axios.isAxiosError(error) ? error.response?.data : undefined;
  const field = (body as { details?: { field?: unknown } } | undefined)?.details?.field;
  return typeof field === 'string' ? field : undefined;
}

export interface CardPolicy {
  operatorName: string;
  maxInstallments: number;
  feeBasisPoints: Array<number | null>;
  firstReceiptDays: number;
  receiptIntervalDays: number;
  anticipationEnabled: boolean;
  anticipationDays: number;
  anticipationBasisPoints: number;
  anticipationFormula: 'FLAT' | 'PRO_RATA';
  anticipationPeriodDays: number;
}
export interface CardPlan {
  policy: CardPolicy;
  installmentCount: number;
  anticipated: boolean;
  installments: Array<{
    number: number;
    dueOn: string;
    grossCents: number;
    cardFeeCents: number;
    anticipationFeeCents: number;
    netCents: number;
  }>;
}
export interface EffectiveCardPolicy {
  policy: CardPolicy;
  inherited: boolean;
  versionId: string | null;
}
export const cardsApi = {
  policy: async (scope: 'COMPANY' | 'UNIT'): Promise<EffectiveCardPolicy> =>
    (await api.get<EffectiveCardPolicy>('/tenant/finance/cards/policy', { params: { scope } }))
      .data,
  save: async (
    scope: 'COMPANY' | 'UNIT',
    policy: CardPolicy | null,
  ): Promise<EffectiveCardPolicy> =>
    (await api.put<EffectiveCardPolicy>('/tenant/finance/cards/policy', { scope, policy })).data,
  salePolicy: async (): Promise<{ installmentCounts: number[] }> =>
    (await api.get<{ installmentCounts: number[] }>('/tenant/finance/cards/sale-policy')).data,
  preview: async (input: {
    amountCents: number;
    soldAt: string;
    installmentCount: number;
    anticipated: boolean;
  }): Promise<CardPlan> => (await api.post<CardPlan>('/tenant/finance/cards/preview', input)).data,
};

export const financialClassificationApi = {
  options: async (
    kind: 'OBLIGATION' | 'RECURRENCE',
    id?: string,
  ): Promise<{
    accounts: Array<{ id: string; code: string; name: string; active: boolean }>;
    currentAccountId: string | null;
  }> =>
    (
      await api.get<{
        accounts: Array<{ id: string; code: string; name: string; active: boolean }>;
        currentAccountId: string | null;
      }>('/tenant/accounting/form-options', { params: { kind, ...(id ? { id } : {}) } })
    ).data,
};

export interface PaymentIncomeTarget {
  sourceKey: string;
  entity: string;
  externalId: string;
  occurrence: string;
}
export interface PaymentReportRow {
  targetDescription?: string | null;
  receiptDays?: number | null;
  operatorName: string;
  operatorKey: string;
  externalId: string;
  occurredOn: string;
  method: 'CARD' | 'PIX' | 'OTHER';
  cardMode: 'CREDIT' | 'DEBIT' | null;
  currency: Currency;
  grossCents: number;
  feeCents: number;
  netCents: number;
  financeAccountName: string;
  target: PaymentIncomeTarget | null;
  proposedTarget: PaymentIncomeTarget | null;
  duplicate: boolean;
  payouts: Array<{ number: number; dueOn: string; netCents: number }>;
}
export interface PaymentReportPreview {
  source: string;
  fingerprint: string;
  rows: PaymentReportRow[];
  incomeOptions: Array<{
    target: PaymentIncomeTarget;
    description: string;
    currency: Currency;
    availableCents: number;
  }>;
}
export interface PaymentReportSetting {
  id: string;
  operatorKey: string;
  operatorName: string;
  businessUnitId: string | null;
  receiptDays: number | null;
  financeAccountId: string | null;
}
export interface PaymentReportBatch {
  id: string;
  fileName: string;
  source: string;
  createdAt: string;
  canceledAt: string | null;
  canceledReason: string | null;
}
export interface PaymentReportInput {
  fileName: string;
  contentBase64: string;
  targets: Record<string, PaymentIncomeTarget | null>;
}
export interface PaymentReportDetail {
  fileName: string;
  rows: PaymentReportRow[];
  payouts: Array<{
    operatorKey: string;
    externalId: string;
    number: number;
    netCents: number;
    receivedCents: number;
  }>;
}
export interface PaymentReportSettings {
  settings: PaymentReportSetting[];
  accounts: Array<{ id: string; displayName: string; currency: Currency }>;
}
export const paymentReportsApi = {
  detail: async (
    id: string,
  ): Promise<{
    fileName: string;
    rows: PaymentReportRow[];
    payouts: Array<{
      operatorKey: string;
      externalId: string;
      number: number;
      netCents: number;
      receivedCents: number;
    }>;
  }> => (await api.get<PaymentReportDetail>(`/tenant/finance/payment-reports/${id}`)).data,
  fileInput: async (file: File): Promise<PaymentReportInput> => ({
    fileName: file.name,
    contentBase64: await fileBase64(file),
    targets: {},
  }),
  template: async (): Promise<Blob> =>
    (await api.get<Blob>('/tenant/finance/payment-reports/template', { responseType: 'blob' }))
      .data,
  settings: async (): Promise<{
    settings: PaymentReportSetting[];
    accounts: Array<{ id: string; displayName: string; currency: Currency }>;
  }> => (await api.get<PaymentReportSettings>('/tenant/finance/payment-reports/settings')).data,
  saveSetting: async (input: {
    operatorName: string;
    scope: 'COMPANY' | 'UNIT';
    receiptDays: number | null;
    financeAccountId: string | null;
  }): Promise<PaymentReportSetting> =>
    (await api.put<PaymentReportSetting>('/tenant/finance/payment-reports/settings', input)).data,
  preview: async (input: PaymentReportInput): Promise<PaymentReportPreview> =>
    (await api.post<PaymentReportPreview>('/tenant/finance/payment-reports/preview', input)).data,
  confirm: async (
    input: PaymentReportInput & { submissionId: string; fingerprint: string },
  ): Promise<{ id: string; repeated: boolean }> =>
    (await api.post<{ id: string; repeated: boolean }>('/tenant/finance/payment-reports', input))
      .data,
  list: async (
    search: string,
    offset: number,
  ): Promise<{ items: PaymentReportBatch[]; total: number }> =>
    (
      await api.get<{ items: PaymentReportBatch[]; total: number }>(
        '/tenant/finance/payment-reports',
        { params: { search, offset, limit: 25 } },
      )
    ).data,
  cancel: async (id: string, reason: string): Promise<{ canceled: boolean }> =>
    (
      await api.post<{ canceled: boolean }>(`/tenant/finance/payment-reports/${id}/cancel`, {
        reason,
      })
    ).data,
};

export interface GuideStepMarkView {
  stepKey: string;
  status: GuideMarkStatus;
  markedAt: string;
  markedByName: string | null;
}

/// O progresso humano dos roteiros, por unidade e mês (ADR 0084).
export const guidesApi = {
  marks: async (guideKey: string, month: string): Promise<GuideStepMarkView[]> =>
    (
      await api.get<{ marks: GuideStepMarkView[] }>(`/tenant/guides/${guideKey}/marks`, {
        params: { month },
      })
    ).data.marks,
  mark: async (
    guideKey: string,
    input: { month: string; stepKey: string; status: GuideMarkStatus },
  ): Promise<void> => {
    await api.put(`/tenant/guides/${guideKey}/marks`, input);
  },
  unmark: async (guideKey: string, input: { month: string; stepKey: string }): Promise<void> => {
    await api.delete(`/tenant/guides/${guideKey}/marks`, { params: input });
  },
};
